import {
  Container,
  Graphics,
  Text,
  TextStyle,
} from "../../../vendor/pixi.min.mjs";
import { BaseScene } from "../../scenes/BaseScene.js";
import { SnakeBody } from "./SnakeBody.js";
import { SnakeFood } from "./SnakeFood.js";
import { SnakeObstacles } from "./SnakeObstacles.js";
import { SnakePerks } from "./SnakePerks.js";
import { SnakeBoardIntro } from "./SnakeBoardIntro.js";
import { SnakeClouds } from "./SnakeClouds.js";
import { SnakeAdventure } from "./SnakeAdventure.js";
import { SnakeTopBar } from "./SnakeTopBar.js";
import { SnakeControls } from "./SnakeControls.js";
import { SnakeBottomCards } from "./SnakeBottomCards.js";
import { FroggerPickupBurst } from "../frogger/FroggerPickupBurst.js";
import { SnakePortalConfetti } from "./SnakePortalConfetti.js";
import { SnakeHouseVortex } from "./SnakeHouseVortex.js";
import { GameOverOverlay, STAT_ICONS } from "../../ui/GameOverOverlay.js";
import { createThemeBackground } from "../../ui/createThemeBackground.js";
import { ScoreSubmitOverlay } from "../../ui/ScoreSubmitOverlay.js";
import { createCountdownLabel } from "../../ui/shared.js";
import { SceneChrome } from "../../ui/SceneChrome.js";
import { SceneAudio } from "../../core/SceneAudio.js";
import { t } from "../../config/i18n.js";
import {
  SNAKE_CONFIG,
  SNAKE_UI,
  computeGrid,
  computeLargeGrid,
} from "./snakeConfig.js";
import { FilterPipeline } from "../../systems/GameFilters.js";
import * as Classic from "./SnakeMode_Classic.js";
import * as Adventure from "./SnakeMode_Adventure.js";
import * as Stations from "./SnakeMode_Stations.js";
import * as FreeRoam from "./SnakeMode_FreeRoam.js";

const {
  BASE_TICK_RATE,
  TICK_RATE_STEP,
  MAX_TICK_RATE,
  INITIAL_LENGTH,
  POINTS_PER_FOOD,
  BOARD_CLEAR_BONUS,
  STATION_POINTS_PER_PASSENGER,
  STATION_GROW_PER_PASSENGER,
  EXIT_PULSE_SPEED,
  OBSTACLE_COUNT_MIN,
  OBSTACLE_COUNT_MAX,
  OBSTACLE_EXTRA_PER_BOARD,
  OBSTACLE_CAP,
  OBSTACLE_SPAWN_MARGIN,
  GHOST_ALPHA,
  SLOW_MULTIPLIER,
  SLOW_MIN_RATE,
  SPEED_UP_STEPS,
  SPEED_UP_TRAIL_MS,
  ADVENTURE_HOUSE_W,
  ADVENTURE_HOUSE_H,
  FREEROAM_OBSTACLE_DENSITY,
  INVINCIBLE_ALPHA,
} = SNAKE_CONFIG;

// Snake audio cues — all client-supplied assets under the snake
// sounds folder.
const POWERUP_SFX_URL = "assets/snake/sounds/powerups-sound.mp3";
const STATION_PICKUP_SFX_URL = "assets/snake/sounds/coin-star.mp3";
const COUNTDOWN_SFX_URL = "assets/snake/sounds/countdown.mp3";
const BACKGROUND_AMBIENCE_URL = "assets/snake/sounds/background-ambiance.mp3";
const PORTAL_SFX_URL = "assets/snake/sounds/portal.mp3";
// Whoosh fired the instant the snake's head crosses the exit portal
// into the next board (distinct from PORTAL_SFX_URL, which announces a
// portal *appearing*).
const PORTAL_ENTER_SFX_URL = "assets/snake/sounds/into-portal.mp3";
// Crash thud — fired on every collision (wall / obstacle / self) the
// moment a life is lost.
const IMPACT_SFX_URL = "assets/snake/sounds/impact.mp3";
// Extra "whoosh" layered ON TOP of the generic perk pickup cue when the
// SPEED perk specifically is collected — both play at once.
const SPEED_SFX_URL = "assets/snake/sounds/speed.mp3";

/**
 * Deep blue surround painted behind every other layer. This is the
 * colour the player sees in the strip of the screen that ISN'T the
 * play arena — behind the top status bar, around the D-pad and the
 * perk legend cards, and along the side margins. Deliberately the
 * same hex as `SnakeTopBar.BAR_BG_COLOR` (0x004f93) so the top bar
 * blends seamlessly into the surrounding chrome.
 */
const SCREEN_BG_COLOR = 0x004f93;
/**
 * Default solid fill *inside* the play arena rectangle. Drawn by
 * `_drawBackground` only when no custom `snake.background` theme image
 * has been uploaded — if a theme bg is present, it shows through here
 * instead so the user's artwork isn't obscured.
 */
const ARENA_BG_COLOR = 0x006dc8;
/** Room reserved in the MIDDLE of the bottom strip for the on-screen
 * D-pad (now centre-anchored — see `SnakeControls._build`). The four
 * perk cards split 2-left / 2-right around this gap. Keep in sync
 * with `SnakeControls`' `PAD_SIZE`. */
const DPAD_RESERVE = 248;

// Game-over highlight window. The instant the snake crashes we set
// `_gameOver = true` so the tick loop bails, but we hold the world
// still for this many frames and pulse the killer cell (obstacle /
// self-segment / wall flash) along with the snake itself so the
// player can see why the run ended before the modal lands. 60 fps
// × 3s = 180 frames.
const SNAKE_GAME_OVER_HOLD_FRAMES = 180;
const SNAKE_HIGHLIGHT_ALPHA_MIN = 0.3;
const SNAKE_HIGHLIGHT_ALPHA_MAX = 1.0;
// Sin frequency of the highlight pulse, in radians per millisecond.
// Tuned for ~4 visible blinks in the 3-second hold:
// 4 × 2π ÷ 3000 ≈ 0.0084 rad/ms.
const SNAKE_HIGHLIGHT_PULSE_SPEED = 0.0084;

const OPPOSITE_SIDE = {
  top: "bottom",
  bottom: "top",
  left: "right",
  right: "left",
};

const ENTRY_DIR = {
  left: { dc: 1, dr: 0 },
  right: { dc: -1, dr: 0 },
  top: { dc: 0, dr: 1 },
  bottom: { dc: 0, dr: -1 },
};

const FX_STYLE = new TextStyle({
  fontFamily: "Arial",
  fontSize: 20,
  fontWeight: "bold",
  fill: "#94a3b8",
});

const MSG_STYLE = new TextStyle({
  fontFamily: "Arial",
  fontSize: 48,
  fontWeight: "bold",
  fill: "#ef4444",
  align: "center",
});

/**
 * Snake game scene with obstacles, multi-board portals, and power-up perks.
 */
export class SnakeScene extends BaseScene {
  _snake = null;
  _food = null;
  _stations = null;
  _obstacles = null;
  _perks = null;

  _grid = { cols: 0, rows: 0, cellSize: 0, originX: 0, originY: 0 };

  /** @type {{ side: string, start: number, width: number } | null} */
  _exit = null;
  _exitGfx = null;

  _score = 0;
  _foodEaten = 0;
  _tickRate = BASE_TICK_RATE;
  _tickAccumulator = 0;
  _gameOver = false;
  _boardNumber = 0;
  _startCountdown = 0;

  /**
   * Per-run tick-rate constants. These start equal to BASE / MAX /
   * STEP from snakeConfig but are scaled down by
   * TOUCH_MODE_TICK_MULTIPLIER when the `touchMode` setting is on, so
   * the snake (and every speed-related effect that derives from these)
   * runs noticeably slower for touchscreen play.
   */
  _baseTickRate = BASE_TICK_RATE;
  _maxTickRate = MAX_TICK_RATE;
  _tickRateStep = TICK_RATE_STEP;

  /** Active timed effects — stored as end-timestamps (0 = inactive).
   *  `invincible` is set by `_loseLife` so collision branches can
   *  ignore obstacles/walls/self while the post-hit grace window is
   *  active. */
  _effects = { ghost: 0, slow: 0, speedTrail: 0, invincible: 0 };
  _preSlowTickRate = 0;
  /** When on, speed_up's boost window lets the snake plough through
   * obstacles (each obstacle explodes in confetti instead of game over). */
  _speedDestroys = false;
  /** When on, snake (and every speed-related multiplier) runs slower
   * — primarily for thumb / touchscreen input. */
  _touchMode = false;

  // ── Lives system ─────────────────────────────────────────
  /** Lives the player gets when a stage starts. Set in init() from
   *  the `livesPerStage` setting; reset to this value whenever the
   *  player crosses a portal to the next stage. */
  _livesPerStage = SNAKE_CONFIG.INITIAL_LIVES;
  /** Current lives. Decremented in `_loseLife`; running it down to
   *  0 ends the run via `_showGameOverOverlay`. */
  _lives = SNAKE_CONFIG.INITIAL_LIVES;
  /** Seconds the snake stays invincible after losing a life. Read
   *  from the `invincibleSeconds` setting; falls back to
   *  `INVINCIBLE_AFTER_HIT_SECONDS`. */
  _invincibleSeconds = SNAKE_CONFIG.INVINCIBLE_AFTER_HIT_SECONDS;
  /** @type {FroggerPickupBurst|null} — shared confetti system (reused
   * from Frogger; the class is game-agnostic despite its name). */
  _pickupBurst = null;
  /** @type {import('../../core/SceneAudio.js').SceneAudio|null} */
  _audio = null;
  /** @type {SnakePortalConfetti|null} — continuous "sucked into the
   * portal" funnel emitter. Geometry is set by `Classic.drawExit`
   * (Classic mode) or `Adventure.drawDoor` (Adventure / Stations). */
  _portalConfetti = null;
  /** @type {SnakeHouseVortex|null} — black-hole confetti swirl that
   * twists around the enterable end building so the player knows to
   * dive in. Centre is re-anchored each frame in `update`. */
  _houseVortex = null;
  /** True when a board intro is in flight and we still owe the station
   * appear-confetti once the intro finishes. Set by `_playBoardIntro`,
   * consumed inside `update` exactly once when `isPlaying()` flips false. */
  _pendingStationAppearFx = false;
  /**
   * Intermediate container that the entire game world is parented to so a
   * screen shake offsets the world *only*. The top bar, D-pad and perk
   * cards live on `this.container` directly so they stay rock-steady
   * while the play area jolts.
   * @type {import('../../../vendor/pixi.min.mjs').Container|null}
   */
  _shakeLayer = null;
  /** Active screen-shake state. `until = 0` means no shake in progress. */
  _screenShake = { until: 0, startedAt: 0, magnitude: 0 };

  /**
   * Staggered "stage building" entry animator. Plays once whenever the
   * board is freshly built (init, restart, board transition) so every
   * obstacle / station / food / exit / house drops + pops into place
   * instead of materialising statically. See {@link SnakeBoardIntro}.
   * @type {SnakeBoardIntro|null}
   */
  _boardIntro = null;

  _bgGraphics = null;
  _themeBg = null;
  _screenBg = null;
  /** @type {SnakeTopBar|null} */
  _topBar = null;
  /** @type {SnakeControls|null} */
  _controls = null;
  /** @type {SnakeBottomCards|null} */
  _bottomCards = null;
  _fxText = null;
  _messageText = null;
  _countdownText = null;
  /** @type {SceneChrome|null} */
  _chrome = null;
  _gameOverOverlay = null;
  _winOverlay = null;
  /** @type {ScoreSubmitOverlay|null} */
  _scoreOverlay = null;
  _paused = false;

  // ── Death-freeze (game-over highlight phase) ──────────────────
  // The moment a wall/obstacle/self collision is detected we set
  // `_gameOver = true` to halt the tick loop, but defer the
  // GameOverOverlay by `SNAKE_GAME_OVER_HOLD_FRAMES` frames so the
  // killer cell can pulse — the player gets a visual receipt for
  // the cause of death before the modal slides in.
  _gameOverPending = false;
  _gameOverHoldFrames = 0;
  /** @type {import('../../../vendor/pixi.min.mjs').Container|null} */
  _gameOverHighlightNode = null;
  /** Saved snake-container alpha so the freeze can restore it. */
  _gameOverSnakeBaseAlpha = 1;
  /** Saved highlight node alpha so the freeze can restore it. */
  _gameOverHighlightBaseAlpha = 1;

  /** What `_tickGameOverHighlight` should do when the freeze ends.
   *  `'gameOver'` — pop the GameOverOverlay (last life lost, timeout,
   *                 direct fail).
   *  `'resume'`   — call `_resumeAfterLifeLoss` (lives remaining).
   *  `null`       — uninitialised; treated as 'gameOver'. */
  _postFlashAction = null;

  /** @type {SnakeAdventure|null} */
  _adventure = null;
  _isAdventureMode = false;
  _isStationMode = false;
  /** @type {import('./SnakeMinimap.js').SnakeMinimap|null} */
  _minimap = null;
  _housePos = null;
  _houseGfx = null;
  _doorGfx = null;

  _isFreeRoam = false;
  _smoothMovement = false;
  _tramTracks = false;
  _trailGfx = null;
  _trailCells = [];
  _pendingTrailCell = null;
  _worldContainer = null;
  _freeRoamTimer = 0;
  _freeRoamTarget = 1000;
  _lastTimerDisplay = -1;
  _freeRoamTimerMax = 60;
  _freeRoamHouseRevealed = false;
  _extraFoods = [];

  _onSwipe = null;
  _onTap = null;

  // ── lifecycle ───────────────────────────────────────

  init(game) {
    super.init(game);
    this._destroyed = false;

    this._isAdventureMode =
      game.snakeMode === "adventure" || game.snakeMode === "stations";
    this._isStationMode = game.snakeMode === "stations";
    this._isFreeRoam = game.snakeMode === "freeroam";
    this._smoothMovement = !!game.theme?.getSetting(
      "snake.settings",
      "smoothMovement",
      false,
    );
    this._smoothCorners = !!game.theme?.getSetting(
      "snake.settings",
      "smoothCorners",
      false,
    );
    this._tramTracks = !!game.theme?.getSetting(
      "snake.settings",
      "tramTracks",
      false,
    );
    this._speedDestroys = !!game.theme?.getSetting(
      "snake.settings",
      "speedDestroys",
      false,
    );
    this._touchMode = !!game.theme?.getSetting(
      "snake.settings",
      "touchMode",
      false,
    );
    // Constant Speed: when true, the natural every-5-pickups speed
    // ramp is disabled so screens 3 and 4 don't feel impossibly
    // fast. Temporary perks (Speed+ / Slow Mo) still modify the
    // tick rate — they're intentional gameplay buffs/debuffs.
    this._constantSpeed = !!game.theme?.getSetting(
      "snake.settings",
      "constantSpeed",
      false,
    );
    // Scale every speed knob by TOUCH_MODE_TICK_MULTIPLIER when
    // touch mode is on. The boost-pickup +N step and the slow-mo
    // multiplier both compose off this _tickRateStep / _baseTickRate
    // automatically, so the slowdown reaches every speed mechanic.
    const tickMul = this._touchMode
      ? SNAKE_CONFIG.TOUCH_MODE_TICK_MULTIPLIER
      : 1;
    this._baseTickRate = BASE_TICK_RATE * tickMul;
    this._maxTickRate = MAX_TICK_RATE * tickMul;
    this._tickRateStep = TICK_RATE_STEP * tickMul;
    this._trailCells = [];
    this._trailGfx = null;
    this._pendingTrailCell = null;
    this._score = 0;
    this._foodEaten = 0;
    this._tickRate = this._baseTickRate;
    this._tickAccumulator = 0;
    this._gameOver = false;
    this._boardNumber = 0;
    this._startCountdown = 3;
    this._effects = { ghost: 0, slow: 0, speedTrail: 0, invincible: 0 };
    this._screenShake = { until: 0, startedAt: 0, magnitude: 0 };
    this._preSlowTickRate = 0;
    this._housePos = null;
    this._houseGfx = null;
    this._doorGfx = null;
    this._freeRoamHouseRevealed = false;
    this._extraFoods = [];
    this._worldContainer = null;

    // Lives + invincibility — driven by the snake settings panel.
    // Falls back to SNAKE_CONFIG defaults when no setting is stored
    // (fresh installs, stripped saves, etc.). Lives are initialised
    // here for the first stage; `_transitionBoard` /
    // `Adventure.transitionBoard` reset them to `_livesPerStage`
    // again whenever the player crosses a portal.
    const livesSetting = Number(
      game.theme?.getSetting(
        "snake.settings",
        "livesPerStage",
        SNAKE_CONFIG.INITIAL_LIVES,
      ),
    );
    this._livesPerStage =
      Number.isFinite(livesSetting) && livesSetting > 0
        ? Math.floor(livesSetting)
        : SNAKE_CONFIG.INITIAL_LIVES;
    this._lives = this._livesPerStage;
    const invSetting = Number(
      game.theme?.getSetting(
        "snake.settings",
        "invincibleSeconds",
        SNAKE_CONFIG.INVINCIBLE_AFTER_HIT_SECONDS,
      ),
    );
    this._invincibleSeconds =
      Number.isFinite(invSetting) && invSetting > 0
        ? invSetting
        : SNAKE_CONFIG.INVINCIBLE_AFTER_HIT_SECONDS;

    if (this._isAdventureMode) {
      const pts =
        game.theme?.getSetting("snake.settings", "pointsToPass", 100) || 100;
      this._adventure = new SnakeAdventure(pts);
    } else {
      this._adventure = null;
    }

    if (this._isFreeRoam) {
      this._freeRoamTarget =
        game.theme?.getSetting("snake.settings", "freeroamTarget", 1000) ||
        1000;
      this._freeRoamTimerMax =
        game.theme?.getSetting("snake.settings", "freeroamTimer", 60) || 60;
      this._freeRoamTimer = this._freeRoamTimerMax;
      this._grid = computeLargeGrid(game.width, game.height);
    } else {
      this._grid = computeGrid(game.width, game.height);
    }

    // Lime / olive screen-wide base — sits behind the world container
    // and fills the strip around the play arena (behind the top bar,
    // under the perk cards, beside the D-pad). The play arena itself
    // is repainted blue inside `_drawBackground` so the gameplay
    // region keeps its own colour identity.
    this._drawScreenBackground();

    // Shake layer wraps the entire game world. The HUD overlays (top
    // bar, D-pad, perk cards) are added directly to `this.container`
    // later, so they stay still while the world jolts on Speed+
    // pickup or obstacle destruction.
    this._shakeLayer = new Container();
    this.container.addChild(this._shakeLayer);
    this._screenShake = { until: 0, startedAt: 0, magnitude: 0 };

    if (this._isFreeRoam) {
      this._worldContainer = new Container();
      this._shakeLayer.addChild(this._worldContainer);
    }

    this._createThemeBackground();
    this._drawBackground();

    if (this._isFreeRoam) {
      this._exit = null;
    } else if (this._isAdventureMode) {
      this._generateAdventureExit();
    } else {
      this._generateExit();
    }
    this._drawExit();

    if (this._tramTracks) {
      this._trailGfx = new Graphics();
      this._world.addChild(this._trailGfx);
    }

    this._createSnake();
    this._createObstacles();

    if (this._isAdventureMode && this._adventure.isLastScreen) {
      this._placeHouse();
    }

    this._createPerks();
    if (this._isStationMode) {
      this._createStations();
    } else {
      this._createFood();
    }

    if (this._isFreeRoam) {
      this._spawnExtraFoods();
    }

    this._createPickupBurst();

    // Clouds are added LAST in the world layer so they render on
    // top of every gameplay element (snake, obstacles, perks,
    // stations, pickup confetti) — a natural "sky in front" look.
    // The HUD overlay is added to `this.container` directly below,
    // so it still sits above the clouds and is never obscured.
    this._createClouds();

    this._createHud();

    if (this._isAdventureMode) {
      this._createMinimap();
    }

    this._chrome = new SceneChrome({
      game: this.game,
      host: this.container,
      gameId: "snake",
      getHelp: () => this._buildHelp(),
      onPause: () => {
        this._paused = true;
      },
      onResume: () => {
        this._paused = false;
      },
    });
    this._chrome.attach();

    // On-screen controls — visible D-pad in the bottom-left, plus an
    // invisible 4-zone touch overlay covering the play arena so a tap
    // anywhere on the playfield turns the tram. Both feed the same
    // direction queue as swipes / keys.
    //
    // FreeRoam's grid is a scrolling super-grid; the visible viewport
    // is `viewCols × viewRows` cells wide. Other modes use the full
    // grid, which already matches the visible play area. Either way
    // we anchor the touch zones to {originX, originY} from `_grid`.
    const gridForArena = this._grid;
    const viewCols = gridForArena.viewCols ?? gridForArena.cols;
    const viewRows = gridForArena.viewRows ?? gridForArena.rows;
    const arenaBounds = this._isFreeRoam
      ? {
          // FreeRoam offsets the world container so the viewport sits
          // where computeGrid() would have placed a non-FreeRoam grid.
          x: Math.round(
            (this.game.width - viewCols * gridForArena.cellSize) / 2,
          ),
          y:
            SNAKE_UI.topReserve +
            Math.round(
              (this.game.height -
                SNAKE_UI.topReserve -
                SNAKE_UI.bottomReserve -
                viewRows * gridForArena.cellSize) /
                2,
            ),
          width: viewCols * gridForArena.cellSize,
          height: viewRows * gridForArena.cellSize,
        }
      : {
          x: gridForArena.originX,
          y: gridForArena.originY,
          width: gridForArena.cols * gridForArena.cellSize,
          height: gridForArena.rows * gridForArena.cellSize,
        };

    this._controls = new SnakeControls(
      this.game,
      (dir) => {
        if (this._gameOver || this._startCountdown > 0) return;
        switch (dir) {
          case "up":
            this._snake.setDirection(0, -1);
            break;
          case "down":
            this._snake.setDirection(0, 1);
            break;
          case "left":
            this._snake.setDirection(-1, 0);
            break;
          case "right":
            this._snake.setDirection(1, 0);
            break;
        }
      },
      arenaBounds,
    );
    this.container.addChild(this._controls.container);

    this._bindInput();
    this._renderAll();

    // Cross-game post-process filter stack (CRT, Old Film, Pixelate,
    // Tilt-Shift, God Rays). Attached to the SCENE ROOT so HUD,
    // top bar, perk cards, D-pad and overlays all pass through the
    // filter together — same behaviour as Frogger / Pipeline. Going
    // through `_shakeLayer` instead would leave the HUD unfiltered
    // and look inconsistent across games. No-op if every toggle is
    // OFF (the default).
    this._filterFx = new FilterPipeline(this.game, this.container);

    if (this._isFreeRoam) {
      this._updateCameraTarget();
      this._worldContainer.x = this._cameraTargetX;
      this._worldContainer.y = this._cameraTargetY;
    }

    this._playBoardIntro();

    // Scene audio: powerup pickup cue (pre-warmed so the first perk
    // grab has no load hitch), the looping background ambience that
    // starts the moment the game opens, and the one-shot start
    // countdown beep that plays alongside the 3-2-1 label.
    this._audio = new SceneAudio();
    this._audio.playLoop(BACKGROUND_AMBIENCE_URL, 0.3);
    this._audio.playOneShot(COUNTDOWN_SFX_URL, 0.9);

    // Initial exit portal is already on the board (drawn before the
    // audio existed). Give it the same arrival cue the per-board
    // portals get. Only Classic mode shows a visible portal.
    if (!this._isAdventureMode && !this._isFreeRoam) {
      this._playPortalAppearSfx();
    }
  }

  update(delta) {
    if (this._destroyed || !this._snake) return;
    if (this._gameOver) {
      if (this._gameOverPending) {
        this._tickGameOverHighlight(delta);
      } else {
        if (this._gameOverOverlay) this._gameOverOverlay.update(delta);
        if (this._winOverlay) this._winOverlay.update(delta);
      }
      return;
    }
    if (this._paused) return;

    // Drive the staggered board-build animation. Runs in parallel with
    // the start-of-game countdown so the stage finishes "constructing
    // itself" by the time the snake is allowed to move.
    if (this._boardIntro?.isPlaying()) {
      this._boardIntro.update((delta * 1000) / 60);
    } else if (this._pendingStationAppearFx) {
      // Intro just finished — fire the "station appeared" confetti
      // now that the sprite is at full scale and visible.
      this._pendingStationAppearFx = false;
      this._fireStationAppearFx();
    }

    if (this._startCountdown > 0) {
      this._startCountdown -= delta / 60;
      const remaining = Math.ceil(this._startCountdown);
      if (remaining > 0) {
        this._countdownText.text = String(remaining);
        this._countdownText.visible = true;
      } else {
        this._countdownText.visible = false;
        this._startCountdown = 0;
        this._tickAccumulator = 0;
      }
      return;
    }

    if (this._isFreeRoam) {
      this._freeRoamTimer -= delta / 60;
      if (this._freeRoamTimer <= 0) {
        this._freeRoamTimer = 0;
        this._freeRoamTimeout();
        return;
      }
      const rounded = Math.ceil(this._freeRoamTimer);
      if (rounded !== this._lastTimerDisplay) {
        this._lastTimerDisplay = rounded;
        this._updateHud();
      }
    }

    // Skip the idle alpha-pulses while the intro animation is mid-flight
    // — otherwise the sine wave fights the fade-in and the gate flickers.
    const introPlaying = this._boardIntro?.isPlaying();
    if (this._exitGfx && !introPlaying) {
      this._exitGfx.alpha =
        0.65 + 0.35 * Math.sin(Date.now() * EXIT_PULSE_SPEED);
    }

    const houseActive =
      !!this._houseGfx &&
      (this._adventure?.houseOpen || this._freeRoamHouseRevealed);
    if (houseActive && !introPlaying) {
      this._houseGfx.alpha = 0.7 + 0.3 * Math.sin(Date.now() * 0.006);
    }

    // Black-hole confetti swirl twisting around the enterable end
    // building. Re-anchor on the house centre each frame while it's
    // active (the world can scroll in free roam); clear the target
    // otherwise so in-flight particles taper off cleanly.
    if (this._houseVortex) {
      if (houseActive && !introPlaying && this._housePos) {
        const { cellSize: cs, originX: ox, originY: oy } = this._grid;
        const halfW = (ADVENTURE_HOUSE_W * cs) / 2;
        const halfH = (ADVENTURE_HOUSE_H * cs) / 2;
        const cx = ox + this._housePos.col * cs + halfW;
        const cy = oy + this._housePos.row * cs + halfH;
        this._houseVortex.setCenter(cx, cy, halfW, halfH);
        // Keep the swirl above the (re-)drawn house sprite so it wraps
        // around the building rather than hiding behind it. No-op once
        // it's already the topmost world child.
        const kids = this._world.children;
        if (kids[kids.length - 1] !== this._houseVortex.container) {
          this._world.setChildIndex(
            this._houseVortex.container,
            kids.length - 1,
          );
        }
      } else {
        this._houseVortex.setCenter(null);
      }
      this._houseVortex.update(delta);
    }

    if (this._doorGfx && !introPlaying && this._adventure?.doorUnlocked) {
      this._doorGfx.alpha =
        0.65 + 0.35 * Math.sin(Date.now() * EXIT_PULSE_SPEED);
    }

    if (this._isFreeRoam) this._updateCamera(delta);

    // Drift the decorative cloud layer. Runs even during the start
    // countdown / board intro so the sky always feels alive.
    if (this._clouds) this._clouds.update(delta);

    this._perks.pulse();
    // Slow infinite breathing pulse on any active tram station so
    // the player can read at a glance that it's the interactive
    // target. Gated by `!introPlaying` so the pulse doesn't fight
    // the staggered drop-in animation that builds the board.
    if (this._stations && !introPlaying) this._stations.pulse();
    // Gentle wind sway on trees / bushes / flowerbeds. Same
    // gating — only kicks in once the board has finished building
    // itself, otherwise plants would rotate while still dropping
    // into place from the intro animator.
    if (this._obstacles && !introPlaying) this._obstacles.sway();
    this._tickActiveEffects();
    this._updateScreenShake();
    // Advance the top bar's pulse animation (currently only used
    // by the LIVES chip on life-loss). Cheap no-op when no pulse
    // is in flight.
    this._topBar?.tick();
    if (this._pickupBurst) this._pickupBurst.update(delta);
    // Skip portal-suck emission while the board intro is still
    // dropping the gate into place — otherwise particles swirl
    // around a half-fallen portal and look detached.
    if (this._portalConfetti && !introPlaying)
      this._portalConfetti.update(delta);
    if (this._bottomCards) this._bottomCards.update(delta);

    // Freeze the tram while the staggered board-build animation is
    // running — otherwise the tram glides forward over obstacles
    // that are still mid-drop-in and the player crashes into a
    // building that "wasn't there yet". Zero the tick accumulator
    // each intro frame so we don't queue up a backlog of ticks
    // that would fire all at once the moment the intro ends.
    if (introPlaying) {
      this._tickAccumulator = 0;
      if (this._smoothMovement) this._renderSmooth(0);
      return;
    }

    const secondsPerTick = 60 / this._tickRate;
    this._tickAccumulator += delta;

    if (this._tickAccumulator >= secondsPerTick) {
      this._tickAccumulator -= secondsPerTick;
      this._gameTick();
    }

    if (this._smoothMovement) {
      const lerpT = Math.min(this._tickAccumulator / secondsPerTick, 1);
      this._renderSmooth(lerpT);
    }

    if (this._filterFx) {
      // Tilt-Shift: keep the in-focus disc on the tram head so
      // the player can always see where they're going. Without
      // this push the focus stays at screen centre and most of
      // the arena reads as a soft blur — unplayable.
      if (this._snake?.segments?.length && this._grid) {
        const head = this._snake.segments[0];
        const { cellSize, originX, originY } = this._grid;
        const wx = originX + head.col * cellSize + cellSize / 2;
        const wy = originY + head.row * cellSize + cellSize / 2;
        // FreeRoam scrolls `_worldContainer`; Classic / Stations /
        // Adventure do not. `_shakeLayer.x/.y` is a transient
        // shake offset on Speed+ pickups — small enough that
        // ignoring it keeps the focus from jittering.
        const sx = (this._isFreeRoam ? this._worldContainer.x : 0) + wx;
        const sy = (this._isFreeRoam ? this._worldContainer.y : 0) + wy;
        this._filterFx.setFocus(sx / this.game.width, sy / this.game.height);
      }
      this._filterFx.update(delta);
    }
  }

  destroy() {
    if (this._destroyed) return;
    this._destroyed = true;

    // Drop intro animator references first so a mid-flight update()
    // can't fire after we've started tearing down the world.
    if (this._boardIntro) {
      this._boardIntro.clear();
      this._boardIntro = null;
    }

    const input = this.game?.input;
    if (input) {
      if (this._onSwipe) input.off("swipe", this._onSwipe);
      if (this._onTap) input.off("tap", this._onTap);
    }
    if (this._filterFx) {
      this._filterFx.destroy();
      this._filterFx = null;
    }
    if (this._chrome) {
      this._chrome.destroy();
      this._chrome = null;
    }
    if (this._controls) {
      this._controls.destroy();
      this._controls = null;
    }
    if (this._topBar) {
      this._topBar.destroy();
      this._topBar = null;
    }
    if (this._bottomCards) {
      this._bottomCards.destroy();
      this._bottomCards = null;
    }
    if (this._screenBg) {
      if (this._screenBg.parent)
        this._screenBg.parent.removeChild(this._screenBg);
      this._screenBg = null;
    }
    if (this._gameOverOverlay) {
      this._gameOverOverlay.destroy();
      this._gameOverOverlay = null;
    }
    if (this._winOverlay) {
      this._winOverlay.destroy();
      this._winOverlay = null;
    }
    if (this._scoreOverlay) {
      this._scoreOverlay.destroy();
      this._scoreOverlay = null;
    }
    if (this._minimap) {
      this._minimap.destroy();
      this._minimap = null;
    }

    // Tear down everything that lives inside `_shakeLayer` BEFORE the
    // shake-layer cascade destroy below. Otherwise we end up with stale
    // references (e.g. `_trailGfx`) to Graphics whose internal Pixi
    // context was nulled by the cascade, and a follow-up `.clear()` /
    // `.destroy()` call on them throws.
    if (this._snake) {
      this._snake.destroy();
      this._snake = null;
    }
    if (this._food) {
      this._food.destroy();
      this._food = null;
    }
    if (this._stations) {
      this._stations.destroy();
      this._stations = null;
    }
    if (this._clouds) {
      this._clouds.destroy();
      this._clouds = null;
    }
    if (this._pickupBurst) {
      this._pickupBurst.destroy();
      this._pickupBurst = null;
    }
    if (this._portalConfetti) {
      this._portalConfetti.destroy();
      this._portalConfetti = null;
    }
    if (this._houseVortex) {
      this._houseVortex.destroy();
      this._houseVortex = null;
    }
    if (this._audio) {
      this._audio.destroy();
      this._audio = null;
    }
    for (const ef of this._extraFoods) ef.destroy?.();
    this._extraFoods = [];
    if (this._obstacles) {
      this._obstacles.destroy();
      this._obstacles = null;
    }
    if (this._perks) {
      this._perks.destroy();
      this._perks = null;
    }
    if (this._trailGfx) {
      if (!this._trailGfx.destroyed) {
        this._trailGfx.clear();
        if (this._trailGfx.parent)
          this._trailGfx.parent.removeChild(this._trailGfx);
        this._trailGfx.destroy();
      }
      this._trailGfx = null;
    }
    this._trailCells = [];
    this._pendingTrailCell = null;

    if (this._shakeLayer) {
      if (this._shakeLayer.parent)
        this._shakeLayer.parent.removeChild(this._shakeLayer);
      if (!this._shakeLayer.destroyed)
        this._shakeLayer.destroy({ children: true });
      this._shakeLayer = null;
    }

    this._worldContainer = null;
    this._adventure = null;
    this._onSwipe = null;
    this._onTap = null;
    this._paused = false;
    super.destroy();
  }

  get _world() {
    if (this._isFreeRoam && this._worldContainer) return this._worldContainer;
    return this._shakeLayer || this.container;
  }

  // ── setup helpers ──────────────────────────────────

  _drawBackground() {
    // The screen-wide background is now `SCREEN_BG_COLOR` (lime),
    // so this method is responsible for painting the play arena in
    // its own colour and laying decoration on top:
    //   0. solid `ARENA_BG_COLOR` (blue) fill inside the grid rect —
    //      ONLY when no `snake.background` theme image is uploaded,
    //      otherwise we'd paint over the user's artwork.
    //   1. an optional uploaded `snake.board.border` overlay
    //   2. optional `snake.board.cell` tile sprites if the user uploaded
    //      a per-cell texture
    //   3. a faint white grid (10% alpha) so cells are still readable
    //      against the blue
    //   4. a darker blue rounded frame anchoring the arena
    const { cols, rows, cellSize, originX, originY } = this._grid;
    const gridW = cols * cellSize;
    const gridH = rows * cellSize;
    const g = new Graphics();
    const cellTexture = this.game?.theme?.get("snake.board.cell");
    const borderTexture = this.game?.theme?.get("snake.board.border");

    if (!this._themeBg) {
      g.rect(originX, originY, gridW, gridH);
      g.fill({ color: ARENA_BG_COLOR });
    }

    if (!this._isFreeRoam && borderTexture) {
      const wallPad = 6;
      const borderSprite = this.game?.theme?.createSprite(
        "snake.board.border",
        gridW + wallPad * 2,
        gridH + wallPad * 2,
        { anchorX: 0, anchorY: 0, fitMode: "cover" },
      );
      if (borderSprite) {
        borderSprite.x = originX - wallPad;
        borderSprite.y = originY - wallPad;
        g.addChild(borderSprite);
      }
    }

    if (cellTexture) {
      for (let c = 0; c < cols; c++) {
        for (let r = 0; r < rows; r++) {
          const cellSprite = this.game?.theme?.createSprite(
            "snake.board.cell",
            cellSize,
            cellSize,
            { fitMode: "cover" },
          );
          if (!cellSprite) continue;
          cellSprite.x = originX + c * cellSize + cellSize / 2;
          cellSprite.y = originY + r * cellSize + cellSize / 2;
          cellSprite.alpha = 0.32;
          g.addChild(cellSprite);
        }
      }
    }

    for (let c = 1; c < cols; c++) {
      g.rect(originX + c * cellSize, originY, 1, gridH);
      g.fill({ color: 0xffffff, alpha: 0.1 });
    }
    for (let r = 1; r < rows; r++) {
      g.rect(originX, originY + r * cellSize, gridW, 1);
      g.fill({ color: 0xffffff, alpha: 0.1 });
    }

    // Darker blue rounded frame around the play arena — visually anchors
    // the playfield against the surrounding screen-wide blue background
    // and matches the top status bar's tone for a coherent UI palette.
    // Skipped in Free Roam (the world scrolls and a fixed border would
    // wander off into world-space).
    if (!this._isFreeRoam) {
      const borderPad = 8;
      const frame = new Graphics();
      frame.roundRect(
        originX - borderPad,
        originY - borderPad,
        gridW + borderPad * 2,
        gridH + borderPad * 2,
        14,
      );
      frame.stroke({ color: 0x003d72, width: 8, alignment: 0.5 });
      g.addChild(frame);
    }

    this._bgGraphics = g;
    this._world.addChild(g);
  }

  _createThemeBackground() {
    this._themeBg = createThemeBackground(this.game, "snake.background");
    if (this._themeBg) {
      this._world.addChild(this._themeBg);
    }
  }

  /**
   * Drifting clouds over the top of the play arena. Pure decoration —
   * lives in the world layer so it scrolls with `_worldContainer` in
   * Free Roam (where the clouds drift over the full virtual world).
   * Added LAST in the world layer (after pickup-burst / portal
   * confetti) so the sky renders ON TOP of every gameplay element —
   * the snake, obstacles, stations, perks, and FX all pass under the
   * clouds. The HUD overlays (top bar, D-pad, perk cards, minimap)
   * live in `this.container` directly so they still sit above the
   * clouds and are never obscured.
   */
  _createClouds() {
    const { cols, rows, cellSize, originX, originY } = this._grid;
    const blurred = !!this.game.theme?.getSetting(
      "snake.settings",
      "cloudBlur",
      true,
    );
    this._clouds = new SnakeClouds(
      this.game?.theme,
      {
        x: originX,
        y: originY,
        w: cols * cellSize,
        h: rows * cellSize,
      },
      { blurred },
    );
    this._world.addChild(this._clouds.container);
  }

  _generateExit(excludeSide = null) {
    Classic.generateExit(this, excludeSide);
  }

  _drawExit() {
    Classic.drawExit(this);
  }

  /** Arrival cue fired by `Classic.drawExit` every time a fresh exit
   *  portal is rendered (per-board transitions + restart). The initial
   *  board fires this from `init` instead, since the audio engine isn't
   *  built yet when the first `_drawExit` runs. */
  _playPortalAppearSfx() {
    this._audio?.playOneShot(PORTAL_SFX_URL, 0.4);
  }

  _generateAdventureExit() {
    Adventure.generateExit(this);
  }

  _drawAdventureDoor() {
    Adventure.drawDoor(this);
  }

  _createMinimap() {
    Adventure.createMinimap(this);
  }

  _updateMinimap() {
    Adventure.updateMinimap(this);
  }

  _placeHouse() {
    Adventure.placeHouse(this);
  }

  _drawHouse() {
    Adventure.drawHouse(this);
  }

  _createSnake() {
    const { cols, rows } = this._grid;
    this._snake = new SnakeBody(
      Math.floor(cols / 2),
      Math.floor(rows / 2),
      INITIAL_LENGTH,
      this.game.theme,
    );
    this._snake.smoothCorners = this._smoothCorners;
    this._world.addChild(this._snake.container);
  }

  _createObstacles() {
    this._obstacles = new SnakeObstacles(this.game.theme);
    const count = this._getObstacleCount();
    const excluded = this._buildExcludedSet();
    const { cols, rows } = this._grid;
    this._obstacles.generate(cols, rows, excluded, count);
    this._obstacles.generateMarginDecor(
      cols,
      rows,
      excluded,
      this._getMarginDecorCount(),
    );
    this._world.addChild(this._obstacles.container);
  }

  /**
   * Number of extra trees / bushes / flowerbeds to scatter along the
   * margins of the playfield. Scales with grid perimeter so larger
   * boards (free-roam) get proportionally more edge greenery without
   * the standard 37×16 board getting overwhelmed.
   */
  _getMarginDecorCount() {
    const { cols, rows } = this._grid;
    const perimeter = 2 * (cols + rows);
    const mult = this._getObstacleDensityMultiplier();
    return Math.max(4, Math.round(perimeter * 0.1 * mult));
  }

  _createPerks() {
    // Per-run override of the perk timing constants. Pulled from
    // the snake settings panel (Options → Snake) so each playtest
    // can tune visibility / countdown without code changes; falls
    // back to SNAKE_CONFIG defaults when no setting is stored.
    const lifetimeS = Number(
      this.game.theme?.getSetting(
        "snake.settings",
        "perkLifetimeSeconds",
        SNAKE_CONFIG.PERK_LIFETIME_MS / 1000,
      ),
    );
    const countdownS = Number(
      this.game.theme?.getSetting(
        "snake.settings",
        "perkCountdownSeconds",
        SNAKE_CONFIG.PERK_BLINK_MS / 1000,
      ),
    );
    const lifetimeMs =
      Number.isFinite(lifetimeS) && lifetimeS > 0
        ? lifetimeS * 1000
        : SNAKE_CONFIG.PERK_LIFETIME_MS;
    const blinkMs =
      Number.isFinite(countdownS) && countdownS > 0
        ? countdownS * 1000
        : SNAKE_CONFIG.PERK_BLINK_MS;
    this._perks = new SnakePerks(this.game.theme, {
      lifetimeMs,
      blinkMs,
      // Keep the on-icon 5..1 digit window aligned with the
      // red-phase wobble so both warnings flip on at the same
      // instant.
      countdownWindowMs: blinkMs,
    });
    this._perks.init();
    this._world.addChild(this._perks.container);
  }

  _createFood() {
    const { cols, rows } = this._grid;
    this._food = new SnakeFood(this.game.theme);
    if (this._stations) {
      this._stations.spawn(
        cols,
        rows,
        this._snake,
        this._obstacles,
        this._perks,
        (c, r) => this._isPickupBlocked(c, r),
        this._makeIsReachable(),
      );
    } else {
      this._food.spawn(cols, rows, this._snake, this._obstacles, this._perks);
    }
    this._world.addChild(this._food.graphic);
  }

  _createStations() {
    Stations.createStations(this);
  }

  _spawnExtraFoods() {
    FreeRoam.spawnExtraFoods(this);
  }

  /**
   * Confetti / spark particle system. Lives inside the world container
   * so its particles scroll with the camera in Free Roam mode.
   * Reused from Frogger (the class is game-agnostic despite its name).
   */
  _createPickupBurst() {
    this._pickupBurst = new FroggerPickupBurst();
    this._world.addChild(this._pickupBurst.container);

    // Continuous "sucking into the portal" confetti — kept in its
    // own system because emission is continuous (not one-shot like
    // FroggerPickupBurst) and motion is INWARD with funnel + swirl,
    // which doesn't fit the outward-burst model. Layered ABOVE the
    // exit gate sprite so particles read as flowing INTO the gate.
    this._portalConfetti = new SnakePortalConfetti();
    this._world.addChild(this._portalConfetti.container);

    // Black-hole confetti swirl around the enterable end building.
    // Added LAST so the vortex reads ON TOP of the house sprite,
    // wrapping around it. Idle until `update` hands it a centre once
    // the house opens.
    this._houseVortex = new SnakeHouseVortex();
    this._world.addChild(this._houseVortex.container);
  }

  _cameraTargetX = 0;
  _cameraTargetY = 0;

  _updateCameraTarget() {
    if (
      !this._isFreeRoam ||
      !this._worldContainer ||
      !this._snake?.segments?.length
    )
      return;
    const head = this._snake.segments[0];
    const { cellSize, originX, originY } = this._grid;
    const worldX = originX + head.col * cellSize + cellSize / 2;
    const worldY = originY + head.row * cellSize + cellSize / 2;

    const idealX = this.game.width / 2 - worldX;
    const idealY = this.game.height / 2 - worldY;

    const dzW = this.game.width * 0.28;
    const dzH = this.game.height * 0.28;

    const curX = this._worldContainer.x;
    const curY = this._worldContainer.y;

    const screenHeadX = worldX + curX;
    const screenHeadY = worldY + curY;
    const cx = this.game.width / 2;
    const cy = this.game.height / 2;

    if (screenHeadX < cx - dzW || screenHeadX > cx + dzW) {
      this._cameraTargetX = idealX;
    }
    if (screenHeadY < cy - dzH || screenHeadY > cy + dzH) {
      this._cameraTargetY = idealY;
    }
  }

  _updateCamera(dt) {
    if (!this._isFreeRoam || !this._worldContainer) return;
    const speed = 1 - Math.pow(0.02, dt / 60);
    const dx = this._cameraTargetX - this._worldContainer.x;
    const dy = this._cameraTargetY - this._worldContainer.y;
    if (Math.abs(dx) > 0.5) this._worldContainer.x += dx * speed;
    else this._worldContainer.x = this._cameraTargetX;
    if (Math.abs(dy) > 0.5) this._worldContainer.y += dy * speed;
    else this._worldContainer.y = this._cameraTargetY;

    const vpX = -this._worldContainer.x;
    const vpY = -this._worldContainer.y;
    this._obstacles.cull(vpX, vpY, this.game.width, this.game.height);
  }

  _freeRoamPlaceHouse() {
    FreeRoam.placeHouse(this);
  }

  _formatTimer(seconds) {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return m > 0 ? `${m}:${String(s).padStart(2, "0")}` : `${s}s`;
  }

  _createHud() {
    // Top status bar — four stat chips (PONT / KOCSI / PÁLYA / IDŐ).
    // Adapts to mode via the third chip's label key (see _updateHud()).
    this._topBar = new SnakeTopBar(this.game);
    this.container.addChild(this._topBar.container);

    // Floating effect text (GHOST / SLOW MO countdowns). Sits just under
    // the top bar so it never overlaps the stat chips.
    this._fxText = new Text({ text: "", style: FX_STYLE });
    this._fxText.x = 20;
    this._fxText.y = SnakeTopBar.HEIGHT + 8;
    this._fxText.visible = false;
    this.container.addChild(this._fxText);

    this._updateHud();

    this._messageText = new Text({ text: "", style: MSG_STYLE });
    this._messageText.anchor.set(0.5);
    this._messageText.x = this.game.width / 2;
    this._messageText.y = this.game.height / 2;
    this._messageText.visible = false;
    this.container.addChild(this._messageText);

    this._countdownText = createCountdownLabel(this.game, {
      initial: "3",
      visible: true,
    });
    this.container.addChild(this._countdownText);

    // Bottom perk legend cards (Slow / Speed / Chaos / Ghost). Offset
    // right of the D-pad so the leftmost card never sits under it.
    this._bottomCards = new SnakeBottomCards(this.game, {
      centerReserve: DPAD_RESERVE,
    });
    this.container.addChild(this._bottomCards.container);
  }

  _drawScreenBackground() {
    const g = new Graphics();
    g.rect(0, 0, this.game.width, this.game.height);
    g.fill(SCREEN_BG_COLOR);
    this._screenBg = g;
    this.container.addChild(g);
  }

  _buildHelp() {
    return {
      title: t("snake.help_title"),
      sections: [
        {
          heading: t("help.goal"),
          color: 0xfbbf24,
          body: t("snake.help_goal"),
        },
        {
          heading: t("help.controls"),
          color: 0xfbbf24,
          body: t("snake.help_controls"),
        },
        {
          heading: t("help.tram_cars"),
          color: 0x94a3b8,
          body: t("snake.help_tram"),
        },
        {
          heading: t("help.obstacles"),
          color: 0x94a3b8,
          body: t("snake.help_obstacles"),
        },
        {
          heading: t("help.exit_portal"),
          color: 0xa855f7,
          body: t("snake.help_exit"),
        },
        {
          heading: t("help.perks"),
          color: 0x60a5fa,
          body: t("snake.help_perks"),
        },
        {
          heading: t("help.shrink"),
          color: 0x22c55e,
          body: t("snake.help_shrink"),
        },
        {
          heading: t("help.speed"),
          color: 0xef4444,
          body: t("snake.help_speed"),
        },
        {
          heading: t("help.chaos"),
          color: 0xf97316,
          body: t("snake.help_chaos"),
        },
        {
          heading: t("help.ghost"),
          color: 0x3b82f6,
          body: t("snake.help_ghost"),
        },
        {
          heading: t("help.slow"),
          color: 0x06b6d4,
          body: t("snake.help_slow"),
        },
      ],
    };
  }

  _bindInput() {
    const input = this.game.input;

    this._onSwipe = (e) => {
      if (this._gameOver || this._startCountdown > 0) return;
      switch (e.direction) {
        case "up":
          this._snake.setDirection(0, -1);
          break;
        case "down":
          this._snake.setDirection(0, 1);
          break;
        case "left":
          this._snake.setDirection(-1, 0);
          break;
        case "right":
          this._snake.setDirection(1, 0);
          break;
      }
    };

    this._onTap = () => {
      // Don't shortcut to the score overlay while the death-
      // freeze is still pulsing the killer cell — let the
      // player see the 3-second highlight first.
      if (
        this._gameOver &&
        !this._gameOverPending &&
        !this._scoreOverlay &&
        !this._winOverlay
      ) {
        this._showScoreOverlay();
      }
    };

    input.on("swipe", this._onSwipe);
    input.on("tap", this._onTap);
  }

  // ── game logic ─────────────────────────────────────

  _gameTick() {
    const { cols, rows, originX, originY, cellSize } = this._grid;
    const oldHead = { ...this._snake.head };

    const newHead = this._snake.tick();

    // --- free roam: wrap-around ---
    if (this._isFreeRoam) {
      if (newHead.col < 0) newHead.col = cols - 1;
      else if (newHead.col >= cols) newHead.col = 0;
      if (newHead.row < 0) newHead.row = rows - 1;
      else if (newHead.row >= rows) newHead.row = 0;
      this._snake.segments[0] = newHead;
    }

    // --- wall / exit ---
    if (
      !this._isFreeRoam &&
      (newHead.col < 0 ||
        newHead.col >= cols ||
        newHead.row < 0 ||
        newHead.row >= rows)
    ) {
      if (this._isExitCollision(newHead)) {
        this._transitionBoard();
        return;
      }
      // Wall hit — no obstacle node to highlight, just the
      // snake itself. The freeze loop will pulse the snake
      // alone, which doubles as a "you walked off the edge"
      // signal. Routed through `_loseLife` so the lives system
      // gets a chance to bounce the player back into play
      // before the actual Game Over modal pops.
      if (this._isInvincible()) {
        // Invincibility shouldn't let the player ride off
        // the edge of the world — revert the just-completed
        // tick so the head sits back at the last on-grid
        // cell, then re-render so visuals match. The player
        // keeps the protection window and can pick a new
        // direction.
        this._revertSnakeHead();
        if (typeof this._renderAll === "function") this._renderAll();
        return;
      }
      this._loseLife({ cause: "wall", col: newHead.col, row: newHead.row });
      return;
    }

    const now = Date.now();
    const isGhost = this._effects.ghost > now;
    const isInvincible = this._isInvincible();
    const isBoosting = this._effects.speedTrail > now;

    // --- obstacle collision (skip in ghost mode + invincibility) ---
    if (
      !isGhost &&
      !isInvincible &&
      this._obstacles.occupies(newHead.col, newHead.row)
    ) {
      // During a speed-up boost window, if the player has opted in to
      // "Speed+ Destroys Obstacles", the snake ploughs through and
      // each destroyed obstacle explodes in confetti instead of
      // ending the run.
      if (isBoosting && this._speedDestroys) {
        this._smashObstacleAt(newHead.col, newHead.row);
      } else {
        // Hand the obstacle's render node over to the
        // freeze loop so it can pulse alongside the snake.
        // `nodeAt` returns the live Pixi Container backing
        // the obstacle (no destruction, no removal).
        const node = this._obstacles.nodeAt?.(newHead.col, newHead.row) || null;
        this._loseLife({
          cause: "obstacle",
          col: newHead.col,
          row: newHead.row,
          node,
        });
        return;
      }
    }

    // --- self collision (skip in ghost mode + invincibility) ---
    if (!isGhost && !isInvincible && this._snake.checkSelfCollision()) {
      // Self-collision: head landed on its own body. The
      // colliding cell IS the head cell, so highlighting the
      // snake container alone covers it visually.
      this._loseLife({ cause: "self", col: newHead.col, row: newHead.row });
      return;
    }

    // --- adventure house collision ---
    if (this._isAdventureMode && this._housePos && this._adventure.houseOpen) {
      const houseCells = this._adventure.getHouseCells(this._housePos);
      if (
        houseCells.some((c) => c.col === newHead.col && c.row === newHead.row)
      ) {
        this._adventureWin();
        return;
      }
    }

    // --- free roam house collision ---
    if (this._isFreeRoam && this._freeRoamHouseRevealed && this._housePos) {
      for (let dc = 0; dc < ADVENTURE_HOUSE_W; dc++) {
        for (let dr = 0; dr < ADVENTURE_HOUSE_H; dr++) {
          if (
            newHead.col === this._housePos.col + dc &&
            newHead.row === this._housePos.row + dr
          ) {
            this._freeRoamWin();
            return;
          }
        }
      }
    }

    // --- pickups ---
    let ateFood = false;
    let pickupCount = 0;
    let pickupPoints = 0;
    if (this._isStationMode && this._stations) {
      const passengers = this._stations.checkCollection(
        newHead.col,
        newHead.row,
      );
      if (passengers > 0) {
        this._audio?.playOneShot(STATION_PICKUP_SFX_URL, 0.9);
        this._snake.grow(passengers * STATION_GROW_PER_PASSENGER);
        pickupCount = passengers;
        pickupPoints = passengers * STATION_POINTS_PER_PASSENGER;
        this._score += pickupPoints;
        this._foodEaten += passengers;
        this._stations.spawn(
          cols,
          rows,
          this._snake,
          this._obstacles,
          this._perks,
          (c, r) => this._isPickupBlocked(c, r),
          this._makeIsReachable(),
        );
        this._fireStationAppearFx();
        ateFood = true;
      }
    } else if (
      this._food &&
      newHead.col === this._food.col &&
      newHead.row === this._food.row
    ) {
      this._snake.grow();
      pickupCount = 1;
      pickupPoints = POINTS_PER_FOOD;
      this._score += pickupPoints;
      this._foodEaten++;
      this._food.spawn(cols, rows, this._snake, this._obstacles, this._perks);
      ateFood = true;
    }

    // --- extra foods (free roam) ---
    if (this._isFreeRoam) {
      for (const ef of this._extraFoods) {
        if (newHead.col === ef.col && newHead.row === ef.row) {
          this._snake.grow();
          this._score += POINTS_PER_FOOD;
          this._foodEaten++;
          ef.spawn(cols, rows, this._snake, this._obstacles, this._perks);
          ateFood = true;
        }
      }
    }

    if (ateFood) {
      if (this._isAdventureMode) {
        this._adventure.addFood(pickupCount || 1);
        // `addScore` keeps returning true on every pickup once the
        // threshold is crossed, so capture the prior state to fire
        // the portal cue only on the false → true transition (the
        // moment the door/house actually appears).
        const wasDoorUnlocked = this._adventure.doorUnlocked;
        const wasHouseOpen = this._adventure.houseOpen;
        const unlocked = this._adventure.addScore(
          pickupPoints || POINTS_PER_FOOD,
        );
        if (unlocked) {
          if (this._adventure.isLastScreen && this._adventure.houseOpen) {
            this._drawHouse();
            if (!wasHouseOpen) this._playPortalAppearSfx();
          } else if (this._adventure.doorUnlocked) {
            this._drawAdventureDoor();
            if (!wasDoorUnlocked) this._playPortalAppearSfx();
          }
        }
      }

      if (
        this._isFreeRoam &&
        !this._freeRoamHouseRevealed &&
        this._score >= this._freeRoamTarget
      ) {
        this._freeRoamPlaceHouse();
      }

      // Natural progression speed-up: every 5 pickups bump the
      // tick rate one step (capped at `_maxTickRate`). Skipped
      // entirely when the player has enabled "Constant Speed" in
      // settings — keeps screens 3 and 4 from feeling brutal.
      if (!this._constantSpeed && this._foodEaten % 5 === 0) {
        this._tickRate = Math.min(
          this._maxTickRate,
          this._tickRate + this._tickRateStep,
        );
      }

      this._updateHud();
    }

    // --- perk collection ---
    const perk = this._perks.checkCollection(newHead.col, newHead.row);
    if (perk) {
      this._audio?.playOneShot(POWERUP_SFX_URL, 0.9);
      // SPEED perk gets an extra whoosh layered on top of the generic
      // pickup cue — both cached on separate elements, so they fire
      // simultaneously rather than cutting each other off.
      if (perk.id === "speed_up") {
        this._audio?.playOneShot(SPEED_SFX_URL, 0.9);
      }
      this._applyPerk(perk);
    }

    // --- perk spawning ---
    const isOccupied = (c, r) =>
      this._snake.occupies(c, r) ||
      this._obstacles.occupies(c, r) ||
      this._perks.occupies(c, r) ||
      (this._food && this._food.col === c && this._food.row === r) ||
      (this._stations && this._stations.occupies(c, r));

    this._perks.gameTick(cols, rows, originX, originY, cellSize, isOccupied);

    // Speed-up boost trail: thick red/orange exhaust plume pluming out
    // behind the head every tick the boost is active. Emitted at the
    // cell the snake is *leaving* so the trail visibly stays behind
    // motion. Uses the dedicated `speedTrail` profile (denser, hotter
    // palette) so it reads as actual exhaust, not a faint puff.
    if (this._effects.speedTrail > now && this._pickupBurst) {
      const px = originX + oldHead.col * cellSize + cellSize / 2;
      const py = originY + oldHead.row * cellSize + cellSize / 2;
      this._pickupBurst.burst(px, py, "speedTrail");
    }

    if (this._tramTracks) {
      this._updateTrail(
        oldHead,
        newHead,
        cols,
        rows,
        originX,
        originY,
        cellSize,
      );
    }

    this._renderAll();
  }

  /**
   * The "you just picked up Speed+" punch effect:
   *   • 3 staggered `speedPop` bursts at the snake head — first one
   *     instant, two follow-ups via `setTimeout` for a rolling crescendo.
   *   • A 360 ms screen shake at 22 px magnitude.
   *
   * Bursts are fired through the existing pickup-burst system so they
   * automatically clean up with the scene and scroll with the camera
   * in Free Roam.
   */
  _fireSpeedPickupFx() {
    if (!this._pickupBurst || !this._snake?.segments?.length) return;
    const head = this._snake.segments[0];
    const { cellSize, originX, originY } = this._grid;
    const px = originX + head.col * cellSize + cellSize / 2;
    const py = originY + head.row * cellSize + cellSize / 2;

    this._pickupBurst.burst(px, py, "speedPop");
    // Two follow-ups for a chunkier crescendo. Guard against the
    // scene being torn down mid-stagger.
    const burstAt = (offset) => {
      setTimeout(() => {
        if (this._pickupBurst && !this._gameOver) {
          this._pickupBurst.burst(px, py, "speedPop");
        }
      }, offset);
    };
    burstAt(90);
    burstAt(180);

    this._shakeScreen(360, 22);
  }

  /**
   * Destroy whatever obstacle covers (col, row) and spawn a cool-blue
   * shockwave-style confetti burst at its centre. No-op if nothing is
   * there. Triggered only during a speed-up boost when the destroy-
   * obstacles toggle is on. Uses the `speedSmash` profile (blue/white)
   * instead of `bomb` (red/orange) so the visual reads as a speed
   * shockwave that matches the Speed+ pickup's own identity.
   * Adds a small punctuating screen shake on each smash so the kinetic
   * impact reads through.
   */
  _smashObstacleAt(col, row) {
    if (!this._obstacles) return;
    const removed = this._obstacles.removeAt(col, row);
    if (!removed) return;
    if (this._pickupBurst) {
      this._pickupBurst.burst(removed.centerX, removed.centerY, "speedSmash");
    }
    this._shakeScreen(220, 10);
    this._obstacles.render(
      this._grid.originX,
      this._grid.originY,
      this._grid.cellSize,
    );
  }

  // ── chaos (jolly joker) ────────────────────────────

  /**
   * Roll one of five chaos outcomes uniformly and apply it. Each
   * outcome has its own visual signature so the player can read what
   * happened from the effects alone.
   *
   *   • turn      — flip the snake into a random valid direction.
   *   • shrink    — pop 1–5 random tram cars off the tail with a big
   *                 rainbow "you got lucky" pop at the head.
   *   • speedup   — same boost window + nitro pop as the Speed+ perk.
   *   • slowdown  — same time-warp as the Slow Mo perk.
   *   • wipe      — destroy every obstacle on the board (Frogger-bomb
   *                 style). Stations and the Adventure / Free-Roam
   *                 house are explicitly excluded — those live in
   *                 separate systems (`_stations`, `_housePos`) and
   *                 are never touched here.
   *
   * If "shrink" rolls but the snake is already minimum length, the
   * outcome is rerolled to "turn" so chaos never fires a no-op.
   */
  _applyChaosOutcome() {
    const OUTCOMES = ["turn", "shrink", "speedup", "slowdown", "wipe"];
    let pick = OUTCOMES[Math.floor(Math.random() * OUTCOMES.length)];

    const cartsAvailable = Math.floor(this._snake.segments.length / 2) - 1;
    if (pick === "shrink" && cartsAvailable <= 0) pick = "turn";

    switch (pick) {
      case "turn":
        this._chaosTurn();
        break;
      case "shrink":
        this._chaosShrink(cartsAvailable);
        break;
      case "speedup":
        this._chaosSpeedup();
        break;
      case "slowdown":
        this._chaosSlowdown();
        break;
      case "wipe":
        this._chaosWipe();
        break;
    }
  }

  _chaosTurn() {
    const dirs = [
      { dc: 0, dr: -1 },
      { dc: 0, dr: 1 },
      { dc: -1, dr: 0 },
      { dc: 1, dr: 0 },
    ];
    const cur = this._snake.direction;
    const valid = dirs.filter((d) => !(d.dc === -cur.dc && d.dr === -cur.dr));
    const pick = valid[Math.floor(Math.random() * valid.length)];
    this._snake.direction = { ...pick };
    this._snake._nextDirection = { ...pick };
  }

  _chaosShrink(cartsAvailable) {
    // Random count in [1, min(5, cartsAvailable)] — cap at 5 so a
    // long snake can't be eviscerated in one pickup.
    const n = 1 + Math.floor(Math.random() * Math.min(5, cartsAvailable));
    // Per-cart confetti boom at each removed cart's centre BEFORE we
    // splice them off, so the player visibly sees every cart go away.
    this._explodeTailCarts(n);
    this._snake.segments.splice(-n * 2, n * 2);

    // Celebratory rainbow pop at the snake head — this is "you got
    // lucky" feedback. Two stacked bursts for extra confetti volume.
    const { px, py } = this._headScreenPos();
    if (this._pickupBurst) {
      this._pickupBurst.burst(px, py, "chaosPop");
      this._pickupBurst.burst(px, py, "chaosPop");
    }
    this._shakeScreen(280, 14);
  }

  /**
   * Single blue/white confetti EXPLOSION at the centre of every
   * currently-placed tram station. Fired AFTER `_stations.spawn(...)`
   * to announce a fresh arrival. The ongoing "I'm here, come grab me"
   * cue is provided separately by the station's slow breathing pulse
   * (`SnakeStations.pulse`) — this burst is just the spawn fanfare.
   */
  _fireStationAppearFx() {
    if (!this._pickupBurst || !this._stations || !this._grid) return;
    const { originX: ox, originY: oy, cellSize: cs } = this._grid;
    const list = this._stations._stations || [];
    for (const st of list) {
      const cx = ox + (st.col + st.w / 2) * cs;
      const cy = oy + (st.row + st.h / 2) * cs;
      this._pickupBurst.burst(cx, cy, "stationAppear");
    }
  }

  /**
   * Fire one confetti boom per cart at the rear of the snake. Pass `n`
   * to explode the last N carts (= last N*2 cells). Centres are
   * sampled BEFORE you splice anything off — call this, THEN splice.
   */
  _explodeTailCarts(n) {
    if (!this._pickupBurst || n <= 0) return;
    const segs = this._snake?.segments;
    if (!segs || segs.length < 2) return;
    const { originX: ox, originY: oy, cellSize: cs } = this._grid;
    for (let i = 0; i < n; i++) {
      const segA = segs[segs.length - 1 - i * 2];
      const segB = segs[segs.length - 2 - i * 2];
      if (!segA || !segB) break;
      const cx = ox + ((segA.col + segB.col) / 2 + 0.5) * cs;
      const cy = oy + ((segA.row + segB.row) / 2 + 0.5) * cs;
      this._pickupBurst.burst(cx, cy, "cartPop");
    }
  }

  _chaosSpeedup() {
    // Same path as the Speed+ perk: tick-rate step + boost window +
    // nitro pop fanfare.
    this._tickRate = Math.min(
      this._maxTickRate,
      this._tickRate + this._tickRateStep * SPEED_UP_STEPS,
    );
    this._effects.speedTrail = Date.now() + SPEED_UP_TRAIL_MS;
    this._fireSpeedPickupFx();
  }

  _chaosSlowdown() {
    // Same path as the Slow Mo perk: clamp tick rate, schedule the
    // expiry timestamp, remember the pre-slow rate so we can restore.
    if (this._effects.slow <= Date.now()) {
      this._preSlowTickRate = this._tickRate;
    }
    this._tickRate = Math.max(
      SLOW_MIN_RATE,
      Math.floor(this._tickRate * SLOW_MULTIPLIER),
    );
    // Match the Slow Mo perk's duration (defined in PERK_TYPES). The
    // chaos pick has no durationMs of its own, so we hard-code 5 s
    // here to mirror snakeConfig.PERK_TYPES.slow.durationMs = 5000.
    this._effects.slow = Date.now() + 5000;
  }

  _chaosWipe() {
    if (!this._obstacles || !this._pickupBurst) return;
    const { originX, originY, cellSize } = this._grid;

    // Snapshot positions BEFORE clearing, so we can emit one
    // rainbow burst per destroyed obstacle. Stations and the house
    // are not in `_obstacles` — they live on `_stations` and
    // `_housePos` and are therefore untouched by this loop.
    const items = this._obstacles.getItemsWorld();
    if (items.length === 0) {
      // Nothing to wipe — still give the player *some* feedback
      // so chaos doesn't feel completely silent.
      const { px, py } = this._headScreenPos();
      this._pickupBurst.burst(px, py, "chaosPop");
      this._shakeScreen(220, 10);
      return;
    }

    for (const it of items) {
      this._pickupBurst.burst(it.centerX, it.centerY, "chaosBomb");
    }
    this._obstacles.clear();
    this._obstacles.render(originX, originY, cellSize);

    // One big rainbow pop on the snake head too, so the player
    // immediately sees the perk fired — even if all obstacles are
    // off the visible viewport (Free Roam).
    const { px, py } = this._headScreenPos();
    this._pickupBurst.burst(px, py, "chaosPop");
    this._shakeScreen(520, 28);
  }

  /** Screen-space centre of the snake's current head cell. */
  _headScreenPos() {
    const head = this._snake.segments[0];
    const { cellSize, originX, originY } = this._grid;
    return {
      px: originX + head.col * cellSize + cellSize / 2,
      py: originY + head.row * cellSize + cellSize / 2,
    };
  }

  /**
   * Schedule a screen shake. The shake's amplitude falls off linearly
   * over the requested duration — punchy at the start, quiet at the end.
   * Calling again before a shake completes just retargets the active
   * shake to whichever is currently stronger (so a small shake during a
   * big one doesn't visibly weaken the overall jolt).
   *
   * @param {number} durationMs  how long the shake should last.
   * @param {number} magnitudePx peak per-axis offset, in px.
   */
  _shakeScreen(durationMs, magnitudePx) {
    const now = Date.now();
    const ending = now + durationMs;
    // Keep whichever shake is currently more energetic at this moment.
    if (
      ending > this._screenShake.until ||
      magnitudePx > this._screenShake.magnitude
    ) {
      this._screenShake.startedAt = now;
      this._screenShake.until = ending;
      this._screenShake.magnitude = Math.max(
        magnitudePx,
        this._screenShake.magnitude,
      );
    }
  }

  /** Per-frame: apply the active shake offset (or zero) to the world. */
  _updateScreenShake() {
    if (!this._shakeLayer) return;
    const now = Date.now();
    if (now >= this._screenShake.until) {
      this._shakeLayer.x = 0;
      this._shakeLayer.y = 0;
      this._screenShake.magnitude = 0;
      return;
    }
    const total = Math.max(
      1,
      this._screenShake.until - this._screenShake.startedAt,
    );
    const remaining = this._screenShake.until - now;
    const falloff = remaining / total;
    const mag = this._screenShake.magnitude * falloff;
    this._shakeLayer.x = (Math.random() - 0.5) * 2 * mag;
    this._shakeLayer.y = (Math.random() - 0.5) * 2 * mag;
  }

  // ── perk effects ───────────────────────────────────

  _applyPerk(type) {
    // Pickup confirmation — pulse the matching bottom card so the
    // player can read at a glance which perk just fired. `flash` is
    // a no-op for perks that don't have a card (shrink).
    if (this._bottomCards) this._bottomCards.flash(type.id);

    switch (type.id) {
      case "shrink":
        if (this._snake.segments.length > 2) {
          // Capture the rear cart's world centre BEFORE removing
          // it, then fire a confetti boom there so the player
          // SEES the cart explode out of existence instead of
          // it silently vanishing from the tail.
          this._explodeTailCarts(1);
          this._snake.segments.splice(-2, 2);
        }
        break;

      case "speed_up":
        this._tickRate = Math.min(
          this._maxTickRate,
          this._tickRate + this._tickRateStep * SPEED_UP_STEPS,
        );
        // Light up the timed boost window — confetti trail starts
        // streaming and (if speedDestroys is on) obstacles will
        // explode on contact instead of ending the run.
        this._effects.speedTrail = Date.now() + SPEED_UP_TRAIL_MS;
        // Big turbo nitro pop at the snake head + a chunky screen
        // shake so the moment of pickup *feels* like a kick.
        this._fireSpeedPickupFx();
        break;

      case "random_turn":
        // "Chaos" is a jolly joker — rolls one of five outcomes,
        // each with its own visual flair so the player can tell
        // which one fired (no text label needed).
        this._applyChaosOutcome();
        break;

      case "ghost":
        this._effects.ghost = Date.now() + type.durationMs;
        this._snake.container.alpha = GHOST_ALPHA;
        break;

      case "slow":
        if (this._effects.slow <= Date.now()) {
          this._preSlowTickRate = this._tickRate;
        }
        this._tickRate = Math.max(
          SLOW_MIN_RATE,
          Math.floor(this._tickRate * SLOW_MULTIPLIER),
        );
        this._effects.slow = Date.now() + type.durationMs;
        break;
    }

    this._updateHud();
  }

  _tickActiveEffects() {
    const now = Date.now();

    if (this._effects.ghost > 0 && now >= this._effects.ghost) {
      this._effects.ghost = 0;
      this._snake.container.alpha = 1;
    }

    if (this._effects.slow > 0 && now >= this._effects.slow) {
      this._effects.slow = 0;
      this._tickRate = this._preSlowTickRate || this._baseTickRate;
    }

    if (this._effects.speedTrail > 0 && now >= this._effects.speedTrail) {
      this._effects.speedTrail = 0;
    }

    // Post-hit invincibility — pulses the snake's alpha while
    // active so the player can see they are still protected, then
    // restores full opacity the instant the window closes (unless
    // Ghost is also active, in which case the GHOST_ALPHA dim
    // takes over again).
    if (this._effects.invincible > 0) {
      if (now >= this._effects.invincible) {
        this._effects.invincible = 0;
        if (this._snake?.container) {
          this._snake.container.alpha =
            this._effects.ghost > now ? GHOST_ALPHA : 1;
        }
      } else if (this._snake?.container) {
        // Sin-wave alpha bounce between INVINCIBLE_ALPHA and 1
        // every ~280ms — fast enough to read as "I'm flashing"
        // without strobing the screen.
        const t01 = (Math.sin(now * 0.022) + 1) * 0.5;
        const a = INVINCIBLE_ALPHA + (1 - INVINCIBLE_ALPHA) * t01;
        this._snake.container.alpha = a;
      }
    }

    this._updateFxHud();
  }

  _updateFxHud() {
    // Live countdowns now live on the bottom perk cards (see
    // SnakeBottomCards.setCountdown). The legacy `_fxText` in the
    // top-left is kept around — but permanently hidden — so any
    // existing show/hide logic elsewhere remains a no-op.
    if (this._fxText && this._fxText.visible) this._fxText.visible = false;
    if (!this._bottomCards) return;

    const now = Date.now();
    const sec = (ts) => (ts > now ? Math.ceil((ts - now) / 1000) : 0);

    this._bottomCards.setCountdown("ghost", sec(this._effects.ghost));
    this._bottomCards.setCountdown("slow", sec(this._effects.slow));
    this._bottomCards.setCountdown("speed_up", sec(this._effects.speedTrail));
  }

  // ── exit / board transitions ───────────────────────

  _isExitCollision(head) {
    const exit = this._exit;
    if (!exit) return false;

    if (this._isAdventureMode && !this._adventure.doorUnlocked) return false;

    const { cols, rows } = this._grid;

    switch (exit.side) {
      case "right":
        return (
          head.col >= cols &&
          head.row >= exit.start &&
          head.row < exit.start + exit.width
        );
      case "left":
        return (
          head.col < 0 &&
          head.row >= exit.start &&
          head.row < exit.start + exit.width
        );
      case "bottom":
        return (
          head.row >= rows &&
          head.col >= exit.start &&
          head.col < exit.start + exit.width
        );
      case "top":
        return (
          head.row < 0 &&
          head.col >= exit.start &&
          head.col < exit.start + exit.width
        );
    }
    return false;
  }

  /**
   * Shared helper — repositions the snake at the entry side of a new board.
   * Used by both classic and adventure board transitions.
   */
  _repositionSnakeFromEntry(entrySide) {
    const { cols, rows } = this._grid;
    const snakeLen = this._snake.segments.length;
    const dir = ENTRY_DIR[entrySide];

    let entryCol, entryRow;
    switch (entrySide) {
      case "left":
        entryCol = 0;
        entryRow = Math.floor(rows / 2);
        break;
      case "right":
        entryCol = cols - 1;
        entryRow = Math.floor(rows / 2);
        break;
      case "top":
        entryCol = Math.floor(cols / 2);
        entryRow = 0;
        break;
      case "bottom":
        entryCol = Math.floor(cols / 2);
        entryRow = rows - 1;
        break;
    }

    this._snake.segments.length = 0;
    for (let i = 0; i < snakeLen; i++) {
      this._snake.segments.push({
        col: entryCol - dir.dc * i,
        row: entryRow - dir.dr * i,
      });
    }
    this._snake._prevSegments = this._snake.segments.map((s) => ({ ...s }));
    this._snake.direction = { ...dir };
    this._snake._nextDirection = { ...dir };
    this._snake._growCounter = 0;
  }

  /** Shared helper — clear + regenerate obstacles using the current excluded set. */
  _regenerateObstacles() {
    const { cols, rows } = this._grid;
    this._obstacles.clear();
    const count = this._getObstacleCount();
    const excluded = this._buildExcludedSet();
    this._obstacles.generate(cols, rows, excluded, count);
    this._obstacles.generateMarginDecor(
      cols,
      rows,
      excluded,
      this._getMarginDecorCount(),
    );
  }

  /** Shared helper — clear perks/trail, respawn food/stations, refresh HUD + render.
   *  Callers are expected to invoke {@link _playBoardIntro} immediately
   *  afterwards; that's what schedules the station appear-confetti via
   *  `_pendingStationAppearFx` so it fires AFTER the drop-in animation
   *  rather than at the (still-invisible) station's final position.
   */
  _regeneratePickupsAndRender() {
    const { cols, rows } = this._grid;
    this._perks.clearAll();
    this._clearTrail();

    if (this._stations) {
      this._stations.spawn(
        cols,
        rows,
        this._snake,
        this._obstacles,
        this._perks,
        (c, r) => this._isPickupBlocked(c, r),
        this._makeIsReachable(),
      );
    } else {
      this._food.spawn(cols, rows, this._snake, this._obstacles, this._perks);
    }

    this._tickAccumulator = 0;
    this._updateHud();
    this._renderAll();
  }

  _transitionBoard() {
    // Portal-entry whoosh the instant the head crosses the exit,
    // before either the classic or adventure board swap kicks in.
    this._audio?.playOneShot(PORTAL_ENTER_SFX_URL, 0.6);

    if (this._isAdventureMode) {
      this._adventureTransition();
      return;
    }

    const entrySide = OPPOSITE_SIDE[this._exit.side];

    this._boardNumber++;
    this._score += BOARD_CLEAR_BONUS;

    // Refill the life pool for the new stage. Per client direction
    // every portal crossing is a fresh start with the configured
    // number of lives — running them down on stage 2 doesn't carry
    // over the depleted count from stage 1.
    this._lives = this._livesPerStage;
    this._effects.invincible = 0;

    this._repositionSnakeFromEntry(entrySide);

    this._generateExit(entrySide);
    this._drawExit();

    this._regenerateObstacles();
    this._regeneratePickupsAndRender();
    this._playBoardIntro();
  }

  _adventureTransition() {
    Adventure.transitionBoard(this);
  }

  _adventureWin() {
    Adventure.showWin(this);
  }

  _showAdventureScoreOverlay(finalScore) {
    Adventure.showScoreOverlay(this, finalScore);
  }

  _freeRoamWin() {
    FreeRoam.showWin(this);
  }

  _freeRoamTimeout() {
    FreeRoam.showTimeout(this);
  }

  // ── helpers ────────────────────────────────────────

  _getObstacleDensityMultiplier() {
    const density =
      this.game.theme?.getSetting(
        "snake.settings",
        "obstacleDensity",
        "normal",
      ) || "normal";
    switch (density) {
      case "few":
        return 0.4;
      case "normal":
        return 1.0;
      case "many":
        return 1.8;
      case "max":
        return 3.0;
      default:
        return 1.0;
    }
  }

  _getObstacleCount() {
    const mult = this._getObstacleDensityMultiplier();
    if (this._isFreeRoam) {
      const { cols, rows } = this._grid;
      return Math.floor(cols * rows * FREEROAM_OBSTACLE_DENSITY * mult);
    }
    const base =
      OBSTACLE_COUNT_MIN +
      Math.floor(Math.random() * (OBSTACLE_COUNT_MAX - OBSTACLE_COUNT_MIN + 1));
    const raw = base + this._boardNumber * OBSTACLE_EXTRA_PER_BOARD;
    return Math.min(OBSTACLE_CAP * mult, Math.round(raw * mult));
  }

  _buildExcludedSet() {
    const excluded = new Set();
    const margin = OBSTACLE_SPAWN_MARGIN;

    for (const seg of this._snake.segments) {
      for (let dc = -margin; dc <= margin; dc++) {
        for (let dr = -margin; dr <= margin; dr++) {
          excluded.add(`${seg.col + dc},${seg.row + dr}`);
        }
      }
    }

    // Forward entry sliver — give the snake a couple extra cells of
    // breathing room past the standard ±4 segment halo. Kept
    // deliberately small (single-lane width, short reach) so the
    // board doesn't end up empty on the entry side and crammed on
    // the far side. Previous experiments with a wide 14×7 corridor
    // pushed all obstacles to one half of the screen — really bad
    // balance, especially on smaller adventure boards.
    const head = this._snake.head;
    if (head) {
      const dir = this._snake.direction || { dc: 1, dr: 0 };
      // Extends 3 cells beyond the standard halo (which already
      // covers ±4 around the head), so the player gets ~7 cells of
      // clear lane ahead — enough to react after the intro ends
      // without skewing the obstacle distribution.
      const CORRIDOR_EXTRA = 3;
      const CORRIDOR_START = OBSTACLE_SPAWN_MARGIN + 1;
      const CORRIDOR_WIDTH = 1; // 1 cell each side → 3-cell-wide lane
      for (
        let step = CORRIDOR_START;
        step <= CORRIDOR_START + CORRIDOR_EXTRA;
        step++
      ) {
        const cx = head.col + dir.dc * step;
        const cy = head.row + dir.dr * step;
        for (let perp = -CORRIDOR_WIDTH; perp <= CORRIDOR_WIDTH; perp++) {
          // Lane runs perpendicular to motion: when moving
          // horizontally `perp` walks rows, when moving
          // vertically it walks columns.
          const px = cx + (dir.dr !== 0 ? perp : 0);
          const py = cy + (dir.dc !== 0 ? perp : 0);
          excluded.add(`${px},${py}`);
        }
      }
    }

    if (this._exit) {
      const exitCells = this._getExitEdgeCells();
      for (const cell of exitCells) {
        for (let dc = -2; dc <= 2; dc++) {
          for (let dr = -2; dr <= 2; dr++) {
            excluded.add(`${cell.col + dc},${cell.row + dr}`);
          }
        }
      }
    }

    if (this._housePos && this._adventure) {
      const houseCells = this._adventure.getHouseCells(this._housePos);
      for (const c of houseCells) {
        for (let dc = -2; dc <= 2; dc++) {
          for (let dr = -2; dr <= 2; dr++) {
            excluded.add(`${c.col + dc},${c.row + dr}`);
          }
        }
      }
    }

    // Already-placed obstacles. Without this entry the goal house
    // (`Adventure.pickHousePosition` / `FreeRoam.placeHouse`) and
    // any later obstacle regen / margin-decor pass have no idea
    // about existing obstacle cells and can happily drop something
    // on top — which is exactly how the end-of-level house was
    // landing inside a building / tree. The house picker already
    // pads its own footprint with a 1-cell halo around any excluded
    // cell, so raw obstacle cells (no halo) are enough here.
    if (this._obstacles?._cells) {
      for (const key of this._obstacles._cells) {
        excluded.add(key);
      }
    }

    return excluded;
  }

  _getExitEdgeCells() {
    const exit = this._exit;
    const { cols, rows } = this._grid;
    const cells = [];
    for (let i = 0; i < exit.width; i++) {
      switch (exit.side) {
        case "right":
          cells.push({ col: cols - 1, row: exit.start + i });
          break;
        case "left":
          cells.push({ col: 0, row: exit.start + i });
          break;
        case "bottom":
          cells.push({ col: exit.start + i, row: rows - 1 });
          break;
        case "top":
          cells.push({ col: exit.start + i, row: 0 });
          break;
      }
    }
    return cells;
  }

  /**
   * Build an `isReachable(col, row)` predicate backed by a single
   * flood-fill snapshot. The Set is computed once and captured in the
   * returned closure so the caller (station spawn candidate filter)
   * can probe many cells without re-running the BFS.
   */
  _makeIsReachable() {
    const reachable = this._computeReachableCells();
    return (c, r) => reachable.has(`${c},${r}`);
  }

  /**
   * Flood-fill from the snake's head and return a `Set<"col,row">`
   * of every cell the snake can actually walk into. Walls of the
   * fixed arena and obstacle cells are barriers; everything else
   * (including the door, exit, house, perks, the snake's own body)
   * is treated as walkable for the purposes of computing reach —
   * we just want to know which open-floor cells are accessible.
   *
   * Used by station spawning to guarantee the placed station has at
   * least one perimeter cell the snake can reach. Cheap to compute:
   * the standard arena is ~37×16 cells, ~600 cell visits worst case.
   *
   * In Free Roam (which never enters station mode) the function still
   * returns a sensible set — wrap-around walls aren't modelled here
   * because stations don't spawn there.
   */
  _computeReachableCells() {
    const reachable = new Set();
    if (!this._snake?.segments?.length) return reachable;
    const { cols, rows } = this._grid;
    const head = this._snake.segments[0];
    const startCol = Math.max(0, Math.min(cols - 1, head.col));
    const startRow = Math.max(0, Math.min(rows - 1, head.row));
    const startKey = `${startCol},${startRow}`;
    // If the head somehow sits on top of an obstacle (shouldn't
    // happen but be defensive) just bail — caller will fall back
    // to the un-filtered candidate list.
    if (this._obstacles?.occupies(startCol, startRow)) return reachable;

    const queue = [[startCol, startRow]];
    reachable.add(startKey);
    while (queue.length) {
      const [c, r] = queue.shift();
      const neighbours = [
        [c + 1, r],
        [c - 1, r],
        [c, r + 1],
        [c, r - 1],
      ];
      for (const [nc, nr] of neighbours) {
        if (nc < 0 || nc >= cols || nr < 0 || nr >= rows) continue;
        const key = `${nc},${nr}`;
        if (reachable.has(key)) continue;
        if (this._obstacles?.occupies(nc, nr)) continue;
        reachable.add(key);
        queue.push([nc, nr]);
      }
    }
    return reachable;
  }

  _isPickupBlocked(col, row) {
    if (this._exit) {
      const exitCells = this._getExitEdgeCells();
      if (exitCells.some((cell) => cell.col === col && cell.row === row)) {
        return true;
      }
    }

    if (this._housePos && this._adventure) {
      const houseCells = this._adventure.getHouseCells(this._housePos);
      if (houseCells.some((cell) => cell.col === col && cell.row === row)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Shared teardown for game-over / win states.
   * Replaces the duplicated reset blocks in _setGameOver / _adventureWin /
   * _freeRoamWin / _freeRoamTimeout.
   *
   * `hideHud` defaults to true (immediate teardown — the win/timeout
   * modals expect the HUD gone). Crash-style game-overs pass `false`
   * so the HUD stays visible during the 3-second highlight phase
   * (`_tickGameOverHighlight`); `_showGameOverOverlay` then calls
   * `_hideRunHud()` once the highlight finishes.
   */
  _endGame({ hideHud = true } = {}) {
    this._gameOver = true;
    this._effects = { ghost: 0, slow: 0, speedTrail: 0, invincible: 0 };
    this._screenShake = { until: 0, startedAt: 0, magnitude: 0 };
    if (this._shakeLayer) {
      this._shakeLayer.x = 0;
      this._shakeLayer.y = 0;
    }
    this._snake.container.alpha = 1;
    this._fxText.visible = false;
    this._messageText.visible = false;
    this._countdownText.visible = false;
    if (hideHud) this._hideRunHud();
  }

  /**
   * Hide the in-run HUD (top bar, bottom cards, D-pad, scene chrome,
   * minimap). Pulled out of `_endGame` so the crash-then-highlight
   * flow can keep the HUD on screen during the 3-second highlight
   * phase and only hide it the moment the Game Over modal appears.
   */
  _hideRunHud() {
    if (this._topBar) this._topBar.setVisible(false);
    if (this._bottomCards) this._bottomCards.setVisible(false);
    if (this._controls) this._controls.setVisible(false);
    if (this._chrome) this._chrome.setVisible(false);
    if (this._minimap) this._minimap.container.visible = false;
  }

  /**
   * Game-over trigger. Halts gameplay immediately (`_gameOver = true`
   * via `_endGame`), but holds the GameOverOverlay back for
   * `SNAKE_GAME_OVER_HOLD_FRAMES` frames so the killer cell + the
   * snake itself can pulse — the player gets a moment to read the
   * cause of death (which obstacle did I run into? did I bite my
   * own tail?) before the modal slides in.
   *
   * `info` shape:
   *   { cause: 'obstacle', col, row, node? } — node is the live
   *       Pixi Container of the obstacle (from
   *       `SnakeObstacles.nodeAt`); pulsed in lockstep with the
   *       snake so it reads as a "this is the deadly pair".
   *   { cause: 'self', col, row }            — snake bit itself;
   *       snake-only highlight.
   *   { cause: 'wall', col, row }            — snake walked OOB;
   *       snake-only highlight.
   *   `null` / undefined                     — legacy callers
   *       (timeout, etc.) still work, just without a specific
   *       cell highlight beyond the snake itself.
   */
  _setGameOver(info = null) {
    // Fire the existing teardown FIRST so any active screen
    // shake / FX flags are zeroed before we kick off the new
    // freeze-and-pulse phase. `hideHud:false` keeps the score
    // chips, perk cards, D-pad, back button, etc. on screen
    // during the 3-second highlight phase — UX direction is
    // that the player should still see their final stats while
    // the killer cell + snake pulse. The HUD only disappears
    // once the modal actually pops, in `_showGameOverOverlay`.
    this._endGame({ hideHud: false });

    this._gameOverPending = true;
    this._gameOverHoldFrames = SNAKE_GAME_OVER_HOLD_FRAMES;
    // Direct game-over (timeouts, immediate fail states) skip the
    // life-deduct flow — the overlay opens at the end of the
    // freeze regardless of how many lives are left.
    this._postFlashAction = "gameOver";

    // Pulse the entire snake container so head + body all blink
    // together — the head cell is the colliding cell for every
    // cause type, and pulsing the whole tram-snake reads better
    // than trying to single-out one car.
    if (this._snake?.container) {
      this._gameOverSnakeBaseAlpha = this._snake.container.alpha ?? 1;
    }

    // The obstacle node (if any) is the secondary highlight —
    // its `node.alpha` cycles in sync with the snake. Stash the
    // base alpha so we can restore it once the freeze ends.
    this._gameOverHighlightNode = info?.node || null;
    this._gameOverHighlightBaseAlpha = this._gameOverHighlightNode?.alpha ?? 1;

    // Optional impact burst at the collision cell — re-uses the
    // existing `speedSmash` profile that already fires for
    // boost-destroy obstacle hits, so the visual vocabulary is
    // consistent across "you crashed" and "you ploughed through".
    if (this._pickupBurst && info && this._grid) {
      const { originX, originY, cellSize } = this._grid;
      if (
        Number.isFinite(info.col) &&
        Number.isFinite(info.row) &&
        Number.isFinite(originX) &&
        Number.isFinite(originY) &&
        Number.isFinite(cellSize)
      ) {
        const cx = originX + (info.col + 0.5) * cellSize;
        const cy = originY + (info.row + 0.5) * cellSize;
        this._pickupBurst.burst(cx, cy, "speedSmash");
      }
    }
  }

  /** True while the post-hit invincibility window is active. */
  _isInvincible() {
    return this._effects.invincible > Date.now();
  }

  /**
   * Roll back the most recent snake tick — restore the segment list
   * to its pre-tick snapshot stored by {@link SnakeBody.tick}. Used
   * by `_resumeAfterLifeLoss` so the snake doesn't sit half inside
   * the wall / obstacle / its own body when play resumes.
   *
   * Safe no-op if the snapshot is missing (early ticks, restart).
   */
  _revertSnakeHead() {
    const snake = this._snake;
    if (!snake) return;
    const prev = snake._prevSegments;
    if (!Array.isArray(prev) || prev.length === 0) return;
    snake.segments = prev.map((s) => ({ ...s }));
  }

  /**
   * Crash entry-point that respects the lives system. Decrements
   * `_lives` and triggers the same 3-second freeze-and-pulse
   * highlight as the legacy `_setGameOver` path, but the freeze
   * dispatches to either:
   *
   *   • `_resumeAfterLifeLoss()` — when lives remain, the snake
   *     reverts to its pre-crash position, a 3..1 countdown plays
   *     and the player gets `_invincibleSeconds` of grace, OR
   *   • `_showGameOverOverlay()` — when the last life was just
   *     lost, the run ends.
   *
   * `info` shape mirrors the original `_setGameOver(info)` —
   * `{ cause, col, row, node? }` — so the highlight visuals are
   * identical to the legacy crash flow.
   */
  _loseLife(info = null) {
    // Crash thud the instant we collide (wall / obstacle / self),
    // paired with the freeze-and-pulse highlight below.
    this._audio?.playOneShot(IMPACT_SFX_URL, 0.9);

    this._lives = Math.max(0, this._lives - 1);
    // Update the HEART chip immediately so the player sees the
    // count tick down during the flash, not after it.
    this._updateHud();
    // Punch the LIVES chip itself so the player gets a second,
    // unmissable cue ("hey, that just cost me a life!") on top
    // of the value tick-down. The HUD's per-frame `tick()` (see
    // `update()`) advances the animation from here on.
    this._topBar?.pulseLives();

    // Re-uses the existing crash teardown + freeze. The only
    // difference vs `_setGameOver` is the dispatch flag below,
    // which `_tickGameOverHighlight` consults at the end of the
    // hold to pick between the modal and the resume flow.
    this._endGame({ hideHud: false });

    this._gameOverPending = true;
    this._gameOverHoldFrames = SNAKE_GAME_OVER_HOLD_FRAMES;
    this._postFlashAction = this._lives <= 0 ? "gameOver" : "resume";

    if (this._snake?.container) {
      this._gameOverSnakeBaseAlpha = this._snake.container.alpha ?? 1;
    }
    this._gameOverHighlightNode = info?.node || null;
    this._gameOverHighlightBaseAlpha = this._gameOverHighlightNode?.alpha ?? 1;

    if (this._pickupBurst && info && this._grid) {
      const { originX, originY, cellSize } = this._grid;
      if (
        Number.isFinite(info.col) &&
        Number.isFinite(info.row) &&
        Number.isFinite(originX) &&
        Number.isFinite(originY) &&
        Number.isFinite(cellSize)
      ) {
        const cx = originX + (info.col + 0.5) * cellSize;
        const cy = originY + (info.row + 0.5) * cellSize;
        this._pickupBurst.burst(cx, cy, "speedSmash");
      }
    }
  }

  /**
   * Returns gameplay to a playable state after a non-fatal life
   * loss. Reverts the snake to its pre-crash head position, kicks
   * off the same 3..1 start-of-stage countdown, and grants
   * `_invincibleSeconds` of crash immunity so the player can read
   * the board and re-orient before traffic resumes.
   */
  _resumeAfterLifeLoss() {
    // Step 1 — un-do the move that killed us so the snake
    // continues from the last safe cell. Re-render so the
    // visible sprite matches the (rolled-back) state instantly.
    this._revertSnakeHead();
    if (typeof this._renderAll === "function") {
      this._renderAll();
    }

    // Step 2 — clear the game-over flags set by `_loseLife` /
    // `_endGame` so the main update loop runs again.
    this._gameOver = false;
    this._gameOverPending = false;
    this._postFlashAction = null;
    if (this._snake?.container) this._snake.container.alpha = 1;

    // Step 3 — pre-roll the same 3..1 countdown the player sees
    // at the start of every stage. Existing machinery in
    // `update()` already gates input + ticking on
    // `_startCountdown > 0`, so the snake stays still while the
    // player reads "3 2 1".
    this._startCountdown = 3;
    if (this._countdownText) {
      this._countdownText.text = "3";
      this._countdownText.visible = true;
    }
    // Same 3-2-1 beep the player hears at the start of a stage, so the
    // post-crash count-back is audible too.
    this._audio?.playOneShot(COUNTDOWN_SFX_URL, 0.9);

    // Step 4 — open the invincibility window. The collision
    // branches (`!isGhost && !isInvincible && ...`) treat this
    // as a "no-collision" pass; the alpha pulse in
    // `_tickActiveEffects` makes the protection visible.
    this._effects.invincible = Date.now() + this._invincibleSeconds * 1000;
  }

  /**
   * Per-frame highlight loop while `_gameOverPending` is true.
   * Pulses the snake container + the killer obstacle node (if
   * any) between `SNAKE_HIGHLIGHT_ALPHA_MIN` and
   * `SNAKE_HIGHLIGHT_ALPHA_MAX`. When the hold timer expires it
   * restores both alphas and pops the actual GameOverOverlay.
   */
  _tickGameOverHighlight(delta) {
    this._gameOverHoldFrames -= delta;

    const phase = performance.now() * SNAKE_HIGHLIGHT_PULSE_SPEED;
    const t01 = (Math.sin(phase) + 1) * 0.5;
    const alpha =
      SNAKE_HIGHLIGHT_ALPHA_MIN +
      (SNAKE_HIGHLIGHT_ALPHA_MAX - SNAKE_HIGHLIGHT_ALPHA_MIN) * t01;

    if (this._snake?.container) {
      this._snake.container.alpha = alpha;
    }
    if (this._gameOverHighlightNode) {
      this._gameOverHighlightNode.alpha = alpha;
    }
    // Keep the pickup-burst particles ticking during the hold —
    // the impact puff fired in `_setGameOver` would otherwise
    // freeze in mid-air because the main update path returns
    // early on `_gameOver`.
    if (this._pickupBurst) this._pickupBurst.update(delta);
    // Same reason for the top bar's pulse animation — the
    // LIVES-chip punch is fired by `_loseLife` and needs to
    // play out during this freeze, not after it.
    this._topBar?.tick();

    if (this._gameOverHoldFrames > 0) return;

    if (this._snake?.container) {
      this._snake.container.alpha = this._gameOverSnakeBaseAlpha;
    }
    if (this._gameOverHighlightNode) {
      this._gameOverHighlightNode.alpha = this._gameOverHighlightBaseAlpha;
    }
    this._gameOverHighlightNode = null;
    this._gameOverPending = false;

    // Dispatch — for life-loss crashes with lives remaining, the
    // freeze hands off to `_resumeAfterLifeLoss` (3..1 countdown
    // + invincibility window). For game-overs (last life lost,
    // direct timeouts, etc.) the modal opens. Defaults to
    // 'gameOver' so any legacy `_setGameOver` callers that
    // forget to set `_postFlashAction` still behave correctly.
    const action = this._postFlashAction || "gameOver";
    this._postFlashAction = null;
    if (action === "resume") {
      this._resumeAfterLifeLoss();
    } else {
      this._showGameOverOverlay();
    }
  }

  /** Build + show the DOM Game Over modal. Split out of
   *  `_setGameOver` so the highlight phase can run first. */
  _showGameOverOverlay() {
    // The 3-second crash highlight phase has finished — drop the
    // in-run HUD (`_setGameOver` skipped this on purpose so the
    // score / cards / pad / back stayed visible during the pulse).
    // The Game Over modal now owns the screen.
    this._hideRunHud();
    // Rich overlay: stats cards + explicit MAIN MENU / RETRY buttons.
    // Replaces the legacy fading "Koppints az újrakezdéshez" text — the
    // tap-to-restart input still works as a fallback for anyone who
    // taps the canvas around the panel, but the buttons are now the
    // obvious primary affordance.
    // Pull the same HUD chip icons used in the top bar so the game
    // over stats read as the same family of stats the player saw mid-
    // run. `theme.get(slot)` resolves to a URL path (PNG); falls back
    // to the inline SVG glyphs if the theme is missing (e.g. asset
    // load failed) so the screen never ends up blank.
    const theme = this.game?.theme;
    const cupIcon = theme?.get("snake.hud.score") || STAT_ICONS.trophy;
    const screenIcon = theme?.get("snake.hud.board") || STAT_ICONS.flag;

    this._gameOverOverlay?.destroy();
    this._gameOverOverlay = new GameOverOverlay();
    this._gameOverOverlay.show(this.game.width, this.game.height, {
      title: t("common.game_over"),
      stats: [
        { icon: cupIcon, label: t("common.stat_score"), value: this._score },
        {
          icon: screenIcon,
          label: t("common.stat_board"),
          value: this._boardNumber + 1,
        },
      ],
      actions: [
        {
          kind: "home",
          label: t("common.main_menu"),
          onClick: () => this.game.sceneManager.switchScene("menu"),
        },
        {
          kind: "retry",
          label: t("common.retry"),
          primary: true,
          onClick: () => this._showScoreOverlay(),
        },
      ],
    });
    this.container.addChild(this._gameOverOverlay.container);
  }

  _showScoreOverlay() {
    if (this._gameOverOverlay) {
      this._gameOverOverlay.destroy();
      this._gameOverOverlay = null;
    }

    const score =
      this._isAdventureMode && this._adventure
        ? this._adventure.totalScore
        : this._score;

    const meta =
      this._isAdventureMode && this._adventure
        ? {
            mode: "adventure",
            screens: this._adventure.currentScreen + 1,
            foodEaten: this._adventure.totalFood,
          }
        : {
            board: this._boardNumber + 1,
            foodEaten: this._foodEaten,
          };

    this._scoreOverlay = new ScoreSubmitOverlay();
    this._scoreOverlay.show({
      game: "snake",
      score,
      outcome: "game_over",
      meta,
      theme: this.game.theme,
      onDone: () => {
        this._scoreOverlay = null;
        this._restartGame();
      },
    });
  }

  _restartGame() {
    const { cols, rows } = this._grid;
    const startCol = Math.floor(cols / 2);
    const startRow = Math.floor(rows / 2);

    this._score = 0;
    this._foodEaten = 0;
    this._tickRate = this._baseTickRate;
    this._tickAccumulator = 0;
    this._gameOver = false;
    this._boardNumber = 0;
    this._startCountdown = 3;
    this._effects = { ghost: 0, slow: 0, speedTrail: 0, invincible: 0 };
    this._lives = this._livesPerStage;
    this._screenShake = { until: 0, startedAt: 0, magnitude: 0 };
    if (this._shakeLayer) {
      this._shakeLayer.x = 0;
      this._shakeLayer.y = 0;
    }
    this._preSlowTickRate = 0;
    this._snake.container.alpha = 1;

    if (this._isAdventureMode && this._adventure) {
      this._adventure.reset();
    }

    if (this._isFreeRoam) {
      this._freeRoamTimer = this._freeRoamTimerMax;
      this._freeRoamHouseRevealed = false;
      this._lastTimerDisplay = -1;
    }

    this._messageText.visible = false;
    if (this._topBar) this._topBar.setVisible(true);
    if (this._bottomCards) {
      this._bottomCards.setVisible(true);
      // Wipe any stale countdowns left over from the previous run so
      // the cards start fresh in their description state.
      this._bottomCards.setCountdown("ghost", 0);
      this._bottomCards.setCountdown("slow", 0);
      this._bottomCards.setCountdown("speed_up", 0);
    }
    if (this._controls) this._controls.setVisible(true);
    if (this._chrome) this._chrome.setVisible(true);
    if (this._gameOverOverlay) {
      this._gameOverOverlay.destroy();
      this._gameOverOverlay = null;
    }
    if (this._winOverlay) {
      this._winOverlay.destroy();
      this._winOverlay = null;
    }
    this._countdownText.text = "3";
    this._countdownText.visible = true;
    // Countdown beep on restart too, matching the start-of-game cue.
    this._audio?.playOneShot(COUNTDOWN_SFX_URL, 0.9);

    this._snake.reset(startCol, startRow, INITIAL_LENGTH);

    if (this._doorGfx) {
      this._doorGfx.clear();
      if (this._doorGfx.parent) this._doorGfx.parent.removeChild(this._doorGfx);
      this._doorGfx = null;
    }
    if (this._houseGfx) {
      this._houseGfx.clear();
      if (this._houseGfx.parent)
        this._houseGfx.parent.removeChild(this._houseGfx);
      this._houseGfx = null;
    }
    this._housePos = null;

    if (this._isFreeRoam) {
      this._exit = null;
    } else if (this._isAdventureMode) {
      this._generateAdventureExit();
    } else {
      this._generateExit();
    }
    this._drawExit();

    this._obstacles.clear();
    const count = this._getObstacleCount();
    const excluded = this._buildExcludedSet();
    this._obstacles.generate(cols, rows, excluded, count);
    this._obstacles.generateMarginDecor(
      cols,
      rows,
      excluded,
      this._getMarginDecorCount(),
    );

    this._perks.clearAll();
    this._clearTrail();

    if (this._minimap) {
      this._minimap.update(0);
      this._minimap.container.visible = true;
    }

    if (this._stations) {
      this._stations.spawn(
        cols,
        rows,
        this._snake,
        this._obstacles,
        this._perks,
        (c, r) => this._isPickupBlocked(c, r),
        this._makeIsReachable(),
      );
      // NOTE: station appear-confetti is fired by `_playBoardIntro()`
      // via the `_pendingStationAppearFx` flag so it lines up with
      // the station sprite dropping into view — firing it here would
      // burst confetti at an invisible (scale-0) sprite.
    } else {
      this._food.spawn(cols, rows, this._snake, this._obstacles, this._perks);
    }

    if (this._isFreeRoam) {
      for (const ef of this._extraFoods) {
        ef.spawn(cols, rows, this._snake, this._obstacles, this._perks);
      }
    }

    this._updateHud();
    this._renderAll();
    this._playBoardIntro();
  }

  /**
   * Kick off the staggered "stage building" intro animation. Called
   * from {@link init} / {@link _restartGame} / board transitions —
   * i.e. anywhere the playfield is freshly populated. Pickup respawns
   * (food / stations after a single bite) deliberately skip this so we
   * only animate when the WHOLE board is rebuilt.
   */
  _playBoardIntro() {
    if (!this._boardIntro) this._boardIntro = new SnakeBoardIntro();
    this._boardIntro.reset();

    const items = this._collectBoardIntroItems();
    if (items.length === 0) {
      // No intro to wait for — fire the station appear FX
      // immediately if we're in stations mode and the burst
      // emitter already exists.
      if (this._isStationMode) this._fireStationAppearFx();
      return;
    }

    // Top-to-bottom, left-to-right reading order — gives the
    // satisfying "wave from the top of the screen" feel.
    items.sort((a, b) => a.cy - b.cy || a.cx - b.cx);

    const stagger = SnakeBoardIntro.STAGGER_MS;
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      this._boardIntro.addNode(it.node, it.cx, it.cy, i * stagger);
    }

    // Defer the station "I'm here!" confetti until the intro is
    // done — otherwise the burst fires while the station sprite is
    // still scaled to 0 and the player never sees the cue. The
    // flag is consumed inside `update` once `isPlaying()` flips false.
    if (this._isStationMode) this._pendingStationAppearFx = true;
  }

  /**
   * Gather every freshly rendered "stage piece" (obstacles, station /
   * food, exit gate, house, door) together with its world-space centre
   * so {@link SnakeBoardIntro} can drop + pop each one in.
   *
   * Assumes the renderers have already been called this frame so the
   * underlying display containers are populated.
   */
  _collectBoardIntroItems() {
    const items = [];
    const { originX: ox, originY: oy, cellSize: cs, cols, rows } = this._grid;

    if (this._obstacles?._nodes?.length) {
      for (const entry of this._obstacles._nodes) {
        items.push({
          node: entry.node,
          cx: entry.x + entry.w / 2,
          cy: entry.y + entry.h / 2,
        });
      }
    }

    if (this._stations) {
      // SnakeStations puts each station's inner art (a Sprite with
      // anchor=0.5, OR a Graphics in the fallback path) directly
      // inside `container`. We MUST animate that wrapper container
      // — never the inner sprite, because Sprite.pivot interacts
      // with the 0.5 anchor and would teleport the texture to the
      // world-origin (= invisible). The spawner only ever emits a
      // single station per board, so the lack of per-station
      // stagger is fine; the whole station pops in as one unit.
      const stations = this._stations._stations || [];
      if (stations.length > 0 && this._stations.container.children.length > 0) {
        const st = stations[0];
        items.push({
          node: this._stations.container,
          cx: ox + (st.col + st.w / 2) * cs,
          cy: oy + (st.row + st.h / 2) * cs,
        });
      }
    } else if (this._food?.graphic) {
      items.push({
        node: this._food.graphic,
        cx: ox + (this._food.col + 0.5) * cs,
        cy: oy + (this._food.row + 0.5) * cs,
      });
    }

    if (this._isFreeRoam && this._extraFoods?.length) {
      for (const ef of this._extraFoods) {
        if (!ef?.graphic) continue;
        items.push({
          node: ef.graphic,
          cx: ox + (ef.col + 0.5) * cs,
          cy: oy + (ef.row + 0.5) * cs,
        });
      }
    }

    const exitCentre = this._exit ? this._exitCentre(this._exit) : null;
    if (exitCentre) {
      if (this._exitGfx) items.push({ node: this._exitGfx, ...exitCentre });
      // The adventure door sits at the same opening as the exit, so
      // it shares the same centre.
      if (this._doorGfx) items.push({ node: this._doorGfx, ...exitCentre });
    }

    if (this._houseGfx && this._housePos) {
      items.push({
        node: this._houseGfx,
        cx: ox + (this._housePos.col + ADVENTURE_HOUSE_W / 2) * cs,
        cy: oy + (this._housePos.row + ADVENTURE_HOUSE_H / 2) * cs,
      });
    }

    return items;
  }

  /** World-space centre of the exit/door opening, based on `this._exit`. */
  _exitCentre(ex) {
    const { originX: ox, originY: oy, cellSize: cs, cols, rows } = this._grid;
    switch (ex.side) {
      case "top":
        return { cx: ox + (ex.start + ex.width / 2) * cs, cy: oy };
      case "bottom":
        return { cx: ox + (ex.start + ex.width / 2) * cs, cy: oy + rows * cs };
      case "left":
        return { cx: ox, cy: oy + (ex.start + ex.width / 2) * cs };
      case "right":
        return { cx: ox + cols * cs, cy: oy + (ex.start + ex.width / 2) * cs };
      default:
        return { cx: ox + (cols / 2) * cs, cy: oy + (rows / 2) * cs };
    }
  }

  _renderAll() {
    const { originX, originY, cellSize } = this._grid;
    this._obstacles.render(originX, originY, cellSize);

    if (!this._smoothMovement) {
      this._snake.render(originX, originY, cellSize, 0);
    }

    if (this._stations) {
      this._stations.render(originX, originY, cellSize);
    } else if (this._food) {
      this._food.render(originX, originY, cellSize);
    }

    if (this._isFreeRoam) {
      for (const ef of this._extraFoods) {
        ef.render(originX, originY, cellSize);
      }
      this._updateCameraTarget();
    }

    this._updateMinimap();
  }

  _renderSmooth(lerpT) {
    const { originX, originY, cellSize, cols, rows } = this._grid;
    const wrapCols = this._isFreeRoam ? cols : 0;
    const wrapRows = this._isFreeRoam ? rows : 0;
    this._snake.render(originX, originY, cellSize, lerpT, wrapCols, wrapRows);

    if (this._isFreeRoam) {
      this._updateCameraTarget();
    }
  }

  // ── tram trail tracks ─────────────────────────────

  _updateTrail(fromCell, toCell, cols, rows, ox, oy, cs) {
    const MAX_AGE = 240;

    const step = this._normalizeTrailStep(fromCell, toCell, cols, rows);
    if (step) {
      const { dc, dr } = step;
      const outDir = this._dirToKey(dc, dr);
      const inDir = this._dirToKey(-dc, -dr);

      if (
        this._pendingTrailCell &&
        this._pendingTrailCell.col === fromCell.col &&
        this._pendingTrailCell.row === fromCell.row
      ) {
        this._pendingTrailCell[outDir] = true;
        this._trailCells.push(this._pendingTrailCell);
      } else {
        const entry = {
          col: fromCell.col,
          row: fromCell.row,
          age: 0,
          n: false,
          e: false,
          s: false,
          w: false,
        };
        if (outDir) entry[outDir] = true;
        this._trailCells.push(entry);
      }

      this._pendingTrailCell = {
        col: toCell.col,
        row: toCell.row,
        age: 0,
        n: false,
        e: false,
        s: false,
        w: false,
        [inDir]: true,
      };
    }

    for (let i = this._trailCells.length - 1; i >= 0; i--) {
      this._trailCells[i].age++;
      if (this._trailCells[i].age > MAX_AGE) {
        this._trailCells.splice(i, 1);
      }
    }

    this._renderTrail(ox, oy, cs, MAX_AGE);
  }

  _renderTrail(ox, oy, cs, maxAge) {
    const g = this._trailGfx;
    if (!g) return;
    g.clear();
    g.removeChildren();

    const rg = Math.round(cs * 0.14);
    const rw = Math.max(2, Math.round(cs * 0.07));
    const tw = Math.max(3, Math.round(cs * 0.18));
    const ts = rg * 2 + rw * 6;
    const c = cs / 2;
    // Background bed strip width — clamped to the rail-to-rail span
    // (outer edge of left rail → outer edge of right rail) so the bed
    // never pokes out past the two rails.
    const bedW = rg * 2 + rw;
    // End-cap length on each side of a tie (within the tie footprint).
    const capLen = Math.max(1, Math.round(ts * 0.25));
    // Ties are placed at cs/4 and 3cs/4 (two positions per cell).
    // In-cell gap = cs/2, across-cell gap = cs/4 + cs/4 = cs/2 — so the
    // pitch is uniform (cs/2) and the 2-per-cell repeat is invisible.
    // Halving the tie count vs the old 4-per-cell layout doubles the
    // breathing room between sleepers.
    const TIE_OFFSETS = [cs / 4, (3 * cs) / 4];

    // Palette (three-layer track look)
    //   bedColor     — yellow bed painted UNDER the rails+ties
    //   railColor    — cream rails painted ON TOP of everything
    //   tieMidColor  — cream centre of each cross-tie
    //   tieEndColor  — slightly darker cap at each tip of a cross-tie
    const bedColor = 0xdda729;
    const railColor = 0xf4e3b2;
    const tieMidColor = 0xf4e3b2;
    const tieEndColor = 0xdeb969;

    // Helper — draws one horizontal tie (used by NS-through / N / S
    // segments). cx,cy are the tie centre.
    const drawTieH = (cx, cy, alpha) => {
      const left = cx - ts / 2;
      const top = cy - tw / 2;
      g.rect(left, top, capLen, tw);
      g.fill({ color: tieEndColor, alpha });
      g.rect(left + capLen, top, ts - capLen * 2, tw);
      g.fill({ color: tieMidColor, alpha });
      g.rect(left + ts - capLen, top, capLen, tw);
      g.fill({ color: tieEndColor, alpha });
    };

    // Helper — draws one vertical tie (used by EW-through / E / W
    // segments). cx,cy are the tie centre.
    const drawTieV = (cx, cy, alpha) => {
      const left = cx - tw / 2;
      const top = cy - ts / 2;
      g.rect(left, top, tw, capLen);
      g.fill({ color: tieEndColor, alpha });
      g.rect(left, top + capLen, tw, ts - capLen * 2);
      g.fill({ color: tieMidColor, alpha });
      g.rect(left, top + ts - capLen, tw, capLen);
      g.fill({ color: tieEndColor, alpha });
    };

    // Curve helper — mirrors `drawTrainsimTrack`'s quarter-
    // annulus painter so a snake corner reads as a smoothly
    // bent rail instead of two perpendicular half-strips
    // crashed together at the cell centre. `acx/acy` is the
    // cell-corner the rail wraps around; `startAng/endAng`
    // sweep the 90° arc that connects the two cardinal ports.
    const SEGS = 18;
    const drawCurve = (cellX, cellY, acx, acy, startAng, endAng, alpha) => {
      const rOuter = c + bedW / 2;
      const rInner = c - bedW / 2;
      const rRailL = c - rg;
      const rRailR = c + rg;
      const rCentre = c;
      const lerp = (t) => startAng + (endAng - startAng) * t;

      // BED — quarter annulus (outer forward + inner back).
      const bedPts = [];
      for (let i = 0; i <= SEGS; i++) {
        const a = lerp(i / SEGS);
        bedPts.push(
          cellX + acx + Math.cos(a) * rOuter,
          cellY + acy + Math.sin(a) * rOuter,
        );
      }
      for (let i = SEGS; i >= 0; i--) {
        const a = lerp(i / SEGS);
        bedPts.push(
          cellX + acx + Math.cos(a) * rInner,
          cellY + acy + Math.sin(a) * rInner,
        );
      }
      g.poly(bedPts);
      g.fill({ color: bedColor, alpha });

      // RAILS — two thin arc-strips painted as a ring of
      // trapezoidal quads so the rails follow the curve.
      const railStripe = (rMid) => {
        const r1 = rMid - rw / 2;
        const r2 = rMid + rw / 2;
        for (let i = 0; i < SEGS; i++) {
          const a1 = lerp(i / SEGS);
          const a2 = lerp((i + 1) / SEGS);
          g.poly([
            cellX + acx + Math.cos(a1) * r1,
            cellY + acy + Math.sin(a1) * r1,
            cellX + acx + Math.cos(a2) * r1,
            cellY + acy + Math.sin(a2) * r1,
            cellX + acx + Math.cos(a2) * r2,
            cellY + acy + Math.sin(a2) * r2,
            cellX + acx + Math.cos(a1) * r2,
            cellY + acy + Math.sin(a1) * r2,
          ]);
          g.fill({ color: railColor, alpha });
        }
      };
      railStripe(rRailL);
      railStripe(rRailR);

      // TIES — two radial crossbars at 1/4 and 3/4 of the
      // arc so the cs/2 sleeper pitch still reads through
      // the corner.
      const halfTs = ts / 2;
      const halfTw = tw / 2;
      for (const tFrac of [0.25, 0.75]) {
        const a = lerp(tFrac);
        const tcx = cellX + acx + Math.cos(a) * rCentre;
        const tcy = cellY + acy + Math.sin(a) * rCentre;
        const rx = Math.cos(a);
        const ry = Math.sin(a);
        // perpendicular vector — rail "width" axis
        const px = -ry;
        const py = rx;
        const tieCorner = (sLong, sShort) => ({
          x: tcx + rx * sLong * halfTs + px * sShort * halfTw,
          y: tcy + ry * sLong * halfTs + py * sShort * halfTw,
        });
        const capFrac = ts > 0 ? (capLen * 2) / ts : 0;
        const innerLong = 1 - capFrac;

        const drawTieQuad = (sLong1, sLong2, color) => {
          const p1 = tieCorner(sLong1, -1);
          const p2 = tieCorner(sLong2, -1);
          const p3 = tieCorner(sLong2, 1);
          const p4 = tieCorner(sLong1, 1);
          g.poly([p1.x, p1.y, p2.x, p2.y, p3.x, p3.y, p4.x, p4.y]);
          g.fill({ color, alpha });
        };
        drawTieQuad(-1, -innerLong, tieEndColor);
        drawTieQuad(-innerLong, innerLong, tieMidColor);
        drawTieQuad(innerLong, 1, tieEndColor);
      }
    };

    for (const t of this._trailCells) {
      const alpha = 0.95 * (1 - t.age / maxAge);
      if (alpha <= 0) continue;

      const cellX = ox + t.col * cs;
      const cellY = oy + t.row * cs;
      const hasN = !!t.n;
      const hasS = !!t.s;
      const hasE = !!t.e;
      const hasW = !!t.w;

      const nsThru = hasN && hasS;
      const ewThru = hasE && hasW;

      // ── Corner tile (exactly two perpendicular cardinal
      // ports, no through-pair) ─ render as quarter-annulus
      // matching `drawTrainsimTrack`. Done BEFORE the half-
      // strip branches so we skip them when curving.
      const cardCount =
        (hasN ? 1 : 0) + (hasE ? 1 : 0) + (hasS ? 1 : 0) + (hasW ? 1 : 0);
      const isCurve = cardCount === 2 && !nsThru && !ewThru;
      if (isCurve) {
        if (hasN && hasE)
          drawCurve(cellX, cellY, cs, 0, Math.PI, Math.PI / 2, alpha);
        if (hasE && hasS)
          drawCurve(cellX, cellY, cs, cs, -Math.PI / 2, -Math.PI, alpha);
        if (hasS && hasW)
          drawCurve(cellX, cellY, 0, cs, 0, -Math.PI / 2, alpha);
        if (hasW && hasN) drawCurve(cellX, cellY, 0, 0, Math.PI / 2, 0, alpha);
        continue;
      }

      if (nsThru) {
        g.rect(cellX + c - bedW / 2, cellY, bedW, cs);
        g.fill({ color: bedColor, alpha });
        for (const off of TIE_OFFSETS) {
          drawTieH(cellX + c, cellY + off, alpha);
        }
        g.rect(cellX + c - rg - rw / 2, cellY, rw, cs);
        g.fill({ color: railColor, alpha });
        g.rect(cellX + c + rg - rw / 2, cellY, rw, cs);
        g.fill({ color: railColor, alpha });
      }

      if (ewThru) {
        g.rect(cellX, cellY + c - bedW / 2, cs, bedW);
        g.fill({ color: bedColor, alpha });
        for (const off of TIE_OFFSETS) {
          drawTieV(cellX + off, cellY + c, alpha);
        }
        g.rect(cellX, cellY + c - rg - rw / 2, cs, rw);
        g.fill({ color: railColor, alpha });
        g.rect(cellX, cellY + c + rg - rw / 2, cs, rw);
        g.fill({ color: railColor, alpha });
      }

      // Half-cell directional segments — place the SAME tie offset
      // that would have appeared in this half if the cell were fully
      // through. The N/W half owns TIE_OFFSETS[0] (cs/4) and the
      // S/E half owns TIE_OFFSETS[1] (3cs/4), so the cs/2 rhythm
      // continues unbroken through corners and end caps.
      if (!nsThru && hasN) {
        g.rect(cellX + c - bedW / 2, cellY, bedW, c);
        g.fill({ color: bedColor, alpha });
        drawTieH(cellX + c, cellY + TIE_OFFSETS[0], alpha);
        g.rect(cellX + c - rg - rw / 2, cellY, rw, c);
        g.fill({ color: railColor, alpha });
        g.rect(cellX + c + rg - rw / 2, cellY, rw, c);
        g.fill({ color: railColor, alpha });
      }
      if (!nsThru && hasS) {
        g.rect(cellX + c - bedW / 2, cellY + c, bedW, c);
        g.fill({ color: bedColor, alpha });
        drawTieH(cellX + c, cellY + TIE_OFFSETS[1], alpha);
        g.rect(cellX + c - rg - rw / 2, cellY + c, rw, c);
        g.fill({ color: railColor, alpha });
        g.rect(cellX + c + rg - rw / 2, cellY + c, rw, c);
        g.fill({ color: railColor, alpha });
      }
      if (!ewThru && hasE) {
        g.rect(cellX + c, cellY + c - bedW / 2, c, bedW);
        g.fill({ color: bedColor, alpha });
        drawTieV(cellX + TIE_OFFSETS[1], cellY + c, alpha);
        g.rect(cellX + c, cellY + c - rg - rw / 2, c, rw);
        g.fill({ color: railColor, alpha });
        g.rect(cellX + c, cellY + c + rg - rw / 2, c, rw);
        g.fill({ color: railColor, alpha });
      }
      if (!ewThru && hasW) {
        g.rect(cellX, cellY + c - bedW / 2, c, bedW);
        g.fill({ color: bedColor, alpha });
        drawTieV(cellX + TIE_OFFSETS[0], cellY + c, alpha);
        g.rect(cellX, cellY + c - rg - rw / 2, c, rw);
        g.fill({ color: railColor, alpha });
        g.rect(cellX, cellY + c + rg - rw / 2, c, rw);
        g.fill({ color: railColor, alpha });
      }

      if (!nsThru && !ewThru) {
        g.circle(cellX + c, cellY + c, rg + rw * 2);
        g.fill({ color: tieEndColor, alpha });
      }
    }
  }

  _clearTrail() {
    this._trailCells = [];
    this._pendingTrailCell = null;
    if (this._trailGfx) this._trailGfx.clear();
  }

  _normalizeTrailStep(fromCell, toCell, cols, rows) {
    let dc = toCell.col - fromCell.col;
    let dr = toCell.row - fromCell.row;

    if (this._isFreeRoam) {
      if (dc > 1 && fromCell.col === cols - 1 && toCell.col === 0) dc = 1;
      else if (dc < -1 && fromCell.col === 0 && toCell.col === cols - 1)
        dc = -1;
      if (dr > 1 && fromCell.row === rows - 1 && toCell.row === 0) dr = 1;
      else if (dr < -1 && fromCell.row === 0 && toCell.row === rows - 1)
        dr = -1;
    }

    if (Math.abs(dc) + Math.abs(dr) !== 1) return null;
    return { dc, dr };
  }

  _dirToKey(dc, dr) {
    if (dc === 1) return "e";
    if (dc === -1) return "w";
    if (dr === 1) return "s";
    if (dr === -1) return "n";
    return null;
  }

  _updateHud() {
    if (!this._topBar) return;

    const cars = Math.ceil(this._snake.segments.length / 2);
    // Lives are passed to every update so the new HEART chip stays
    // in sync with `_loseLife` and `_transitionBoard` decrements /
    // resets across all four modes.
    const lives = this._lives;

    if (this._isFreeRoam) {
      // Free Roam: "score / target" + countdown timer.
      this._topBar.update({
        score: `${this._score}/${this._freeRoamTarget}`,
        cars,
        board: this._freeRoamHouseRevealed ? "★" : "—",
        boardLabelKey: "common.stat_target",
        time: this._formatTimer(this._freeRoamTimer),
        lives,
      });
    } else if (this._isAdventureMode && this._adventure) {
      // Adventure: cumulative score, screen N/4 with points needed.
      this._topBar.update({
        score: this._adventure.totalScore,
        cars,
        board: `${this._adventure.currentScreen + 1}/4`,
        boardLabelKey: "common.stat_screen",
        time: "",
        lives,
      });
    } else {
      // Classic / Stations: per-board score, board counter.
      this._topBar.update({
        score: this._score,
        cars,
        board: this._boardNumber + 1,
        boardLabelKey: "common.stat_board",
        time: "",
        lives,
      });
    }
  }
}
